import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UploadSimple, MagnifyingGlass, ClockCounterClockwise, X, PaperPlaneTilt, CheckCircle, Package, DownloadSimple } from "@phosphor-icons/react";

const C = { navy: "#0B1E3B", red: "#E01A2E", cream: "#EFECE4", body: "#F7F5F0", slate: "#5B6B7E", green: "#1E9E63", ink: "#1A1E24", amber: "#F5A623" };
const LIMIT = 25;
const CANCEL_REASONS = ["Non-payment", "Member requested", "Deceased", "Duplicate account", "Relocated / no longer covered"];

// Consumer plan tiers. Uses a real `plan` field when present; otherwise falls back to an
// interim mapping (accurate Single/Duo/Family needs the household-linkage report).
const PLAN_STYLE = {
  single: ["Single", C.navy], couple: ["Duo", C.amber], duo: ["Duo", C.amber],
  family: ["Family", C.red], commercial: ["Commercial", C.navy], demo: ["Demo", C.amber],
};
const planOf = (c) => {
  const key = (c.plan || "").toLowerCase();
  if (PLAN_STYLE[key]) return PLAN_STYLE[key];
  if (c.product === "demo") return PLAN_STYLE.demo;
  if (c.product !== "consumer") return PLAN_STYLE.commercial;
  return c.member_type === "dependant" ? PLAN_STYLE.family : PLAN_STYLE.single;
};

const Kpi = ({ label, value, accent }) => (
  <div style={{ background: "#fff", border: "1px solid rgba(26,30,36,0.08)", borderRadius: 10, padding: "16px 18px", flex: 1 }}>
    <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: C.slate, fontFamily: "'JetBrains Mono', monospace" }}>{label}</div>
    <div style={{ marginTop: 6, fontSize: 26, fontWeight: 700, color: accent || C.ink, fontVariantNumeric: "tabular-nums" }}>{value}</div>
  </div>
);

export default function ContactsPage() {
  const [stats, setStats] = useState(null);
  const [data, setData] = useState({ total: 0, items: [] });
  const [f, setF] = useState({ product: "", insurer: "", has_app: "", cancelled: "false", activation: "", do: "",
    q: new URLSearchParams(window.location.search).get("q") || "" });
  const [page, setPage] = useState(0);
  const [importing, setImporting] = useState(false);
  const [hist, setHist] = useState(null);
  const [upgrades, setUpgrades] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [invite, setInvite] = useState(null);
  const [unmatched, setUnmatched] = useState(null);
  const [unmatchedOpen, setUnmatchedOpen] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [sel, setSel] = useState([]);
  const [pack, setPack] = useState(null);
  const [packBusy, setPackBusy] = useState(false);

  const sendPack = async (ids) => {
    setPackBusy(true);
    try {
      const { data } = await api.post("/contacts/welcome-pack/bulk", { ids });
      setPack(data.results);
      setSel([]);
      loadContacts();
    } catch (e) { toast.error("Couldn't send welcome pack", { description: e.response?.data?.detail || e.message }); }
    finally { setPackBusy(false); }
  };
  const toggleSel = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const sendInvite = async (c) => {
    try {
      const r = await api.post(`/contacts/${c.id}/invite`);
      setInvite(r.data);
      loadContacts();
    } catch (e) { toast.error("Couldn't create invite", { description: e.response?.data?.detail || e.message }); }
  };

  const addPartner = async () => {
    const name = window.prompt("New partner name (e.g. ResQ1 Direct)");
    if (!name || !name.trim()) return;
    try {
      await api.post("/admin/partners", { name: name.trim() });
      toast.success(`Partner "${name.trim()}" added`, { description: "It's now selectable when adding people and in the reconcile / removal tools" });
    } catch (e) { toast.error("Couldn't add partner", { description: e.response?.data?.detail || e.message }); }
  };

  const loadStats = () => api.get("/contacts/stats").then((r) => setStats(r.data));
  const loadUpgrades = () => api.get("/upgrade-requests?status=pending").then((r) => setUpgrades(r.data.items || [])).catch(() => {});
  const loadUnmatched = () => api.get("/help-history/unmatched").then((r) => setUnmatched(r.data)).catch(() => {});
  const loadContacts = () => {
    const p = new URLSearchParams({ limit: LIMIT, skip: page * LIMIT });
    Object.entries(f).forEach(([k, v]) => v && p.append(k, v));
    return api.get(`/contacts?${p}`).then((r) => setData(r.data));
  };

  useEffect(() => { loadStats(); loadUpgrades(); loadUnmatched(); }, []);
  useEffect(() => { loadContacts(); }, [f, page]); // eslint-disable-line

  const toggleSecurity = async (c, i) => {
    const next = !c.security_active;
    try {
      await api.patch(`/contacts/${c.id}/security`, { active: next });
      setData((d) => ({ ...d, items: d.items.map((x, idx) => (idx === i ? { ...x, security_active: next } : x)) }));
    } catch (e) {
      toast.error("Couldn't update security package", { description: e.response?.data?.detail || e.message });
    }
  };

  const resolveUpgrade = async (id, status) => {
    try {
      await api.post(`/upgrade-requests/${id}/resolve`, { status });
      setUpgrades((u) => u.filter((x) => x.id !== id));
      toast.success(status === "approved" ? "Security Package activated" : "Request dismissed");
      if (status === "approved") loadContacts();
    } catch (e) {
      toast.error("Couldn't resolve request", { description: e.response?.data?.detail || e.message });
    }
  };

  const runImport = async () => {
    setImporting(true);
    try {
      const r = await api.post("/contacts/import");
      toast.success(`Imported ${r.data.imported.toLocaleString()} contacts`);
      await Promise.all([loadStats(), loadContacts()]);
    } catch (e) {
      toast.error("Import failed", { description: e.response?.data?.detail || e.message });
    } finally { setImporting(false); }
  };

  const [member, setMember] = useState(null);
  const openHistory = async (c) => {
    try { const r = await api.get(`/contacts/${c.id}/timeline`); setHist(r.data); }
    catch (e) { toast.error("Couldn't load history", { description: e.response?.data?.detail || e.message }); }
  };

  const purgeUnmatched = async () => {
    try {
      const r = await api.delete("/help-history/unmatched");
      toast.success(`Disposed ${r.data.deleted.toLocaleString()} orphaned records`, { description: `${r.data.archived.toLocaleString()} archived (reversible)` });
      setUnmatchedOpen(false);
      loadUnmatched();
    } catch (e) { toast.error("Couldn't dispose records", { description: e.response?.data?.detail || e.message }); }
  };

  const empty = stats && stats.total === 0;

  return (
    <div style={{ background: C.body, minHeight: "100vh", padding: 32 }} data-testid="contacts-page">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 22 }}>
        <div>
          <div style={{ fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: C.slate, fontFamily: "'JetBrains Mono', monospace", display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ width: 22, height: 1, background: C.red }} /> Subscriber base
          </div>
          <h1 style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 34, fontWeight: 500, letterSpacing: "-0.02em", color: C.ink, marginTop: 6 }}>Subscribers</h1>
        </div>
        <button onClick={() => setAddOpen(true)} data-testid="add-subscriber-btn"
          style={{ display: "flex", alignItems: "center", gap: 8, padding: "11px 18px", background: C.red, color: "#fff", border: 0, borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
          + Add subscriber
        </button>
        <button onClick={() => setPurgeOpen(true)} data-testid="purge-person-btn"
          style={{ display: "flex", alignItems: "center", gap: 8, padding: "11px 18px", background: "transparent", color: C.slate, border: `1px solid ${C.slate}44`, borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
          Start a person from scratch
        </button>
        <button onClick={() => setBulkOpen(true)} data-testid="bulk-purge-btn"
          style={{ display: "flex", alignItems: "center", gap: 8, padding: "11px 18px", background: "transparent", color: C.slate, border: `1px solid ${C.slate}44`, borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
          Remove a list of people
        </button>
        <button onClick={() => setReconcileOpen(true)} data-testid="reconcile-active-btn"
          style={{ display: "flex", alignItems: "center", gap: 8, padding: "11px 18px", background: "transparent", color: C.slate, border: `1px solid ${C.slate}44`, borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
          Reconcile an active list
        </button>
        <button onClick={addPartner} data-testid="add-partner-btn"
          style={{ display: "flex", alignItems: "center", gap: 8, padding: "11px 18px", background: "transparent", color: C.slate, border: `1px solid ${C.slate}44`, borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
          + Add partner
        </button>
      </div>

      {upgrades.length > 0 && (
        <div style={{ background: "#fff", border: `1px solid ${C.amber}55`, borderRadius: 12, padding: 18, marginBottom: 18 }} data-testid="upgrade-requests-panel">
          <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: C.amber, fontFamily: "'JetBrains Mono', monospace", marginBottom: 10 }}>
            {upgrades.length} pending upgrade request{upgrades.length === 1 ? "" : "s"}
          </div>
          {upgrades.map((u) => (
            <div key={u.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "10px 0", borderTop: `1px solid ${C.ink}0d` }} data-testid={`upgrade-req-${u.id}`}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.ink }}>{u.name || u.email}</div>
                <div style={{ fontSize: 12, color: C.slate, fontFamily: "'JetBrains Mono', monospace" }}>{[u.email, u.cell, u.insurer].filter(Boolean).join(" · ")}</div>
              </div>
              <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                <button onClick={() => resolveUpgrade(u.id, "approved")} data-testid={`upgrade-approve-${u.id}`}
                  style={{ padding: "7px 14px", background: C.green, color: "#fff", border: 0, borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Activate</button>
                <button onClick={() => resolveUpgrade(u.id, "dismissed")} data-testid={`upgrade-dismiss-${u.id}`}
                  style={{ padding: "7px 14px", background: "transparent", color: C.slate, border: `1px solid ${C.slate}44`, borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Dismiss</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {unmatched && unmatched.row_count > 0 && (
        <div style={{ background: "#fff", border: `1px solid ${C.slate}33`, borderRadius: 12, padding: 16, marginBottom: 18, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }} data-testid="unmatched-banner">
          <div>
            <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: C.slate, fontFamily: "'JetBrains Mono', monospace" }}>Orphaned legacy history</div>
            <div style={{ fontSize: 14, color: C.ink, marginTop: 4 }}>
              <b>{unmatched.row_count.toLocaleString()}</b> help-request record{unmatched.row_count === 1 ? "" : "s"} across <b>{unmatched.people_count.toLocaleString()}</b> {unmatched.people_count === 1 ? "person" : "people"} match no subscriber and are unreachable in the UI.
            </div>
          </div>
          <button onClick={() => setUnmatchedOpen(true)} data-testid="unmatched-review-btn"
            style={{ flexShrink: 0, padding: "10px 16px", background: C.slate, color: "#fff", border: 0, borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Review &amp; dispose</button>
        </div>
      )}

      {empty ? (
        <div style={{ background: "#fff", border: "1px dashed rgba(26,30,36,0.2)", borderRadius: 12, padding: "60px 20px", textAlign: "center" }} data-testid="contacts-empty">
          <div style={{ fontFamily: "'Fraunces', serif", fontSize: 22, color: C.ink }}>No contacts yet</div>
          <p style={{ color: C.slate, fontSize: 14, marginTop: 8, maxWidth: 420, margin: "8px auto 0" }}>Import the 2,922 de-duplicated legacy records from the four insurer exports to get started.</p>
        </div>
      ) : stats && (
        <>
          <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
            <Kpi label="Total contacts" value={stats.total.toLocaleString()} />
            <Kpi label="Have the app" value={stats.has_app.toLocaleString()} accent={C.green} />
            <Kpi label="Consumer" value={stats.consumer.toLocaleString()} accent={C.red} />
            <Kpi label="Commercial" value={stats.commercial.toLocaleString()} accent={C.navy} />
            <Kpi label="Demo" value={(stats.demo || 0).toLocaleString()} accent={C.amber} />
          </div>

          <div style={{ display: "flex", gap: 12, marginBottom: 18, flexWrap: "wrap", alignItems: "center" }}>
            {stats.by_insurer.map((b) => (
              <div key={b.insurer} style={{ background: C.cream, borderRadius: 8, padding: "8px 14px", fontSize: 12, color: C.ink }} data-testid={`insurer-chip-${b.insurer}`}>
                <b>{b.insurer}</b> · {b.count.toLocaleString()} <span style={{ color: C.green }}>({b.app} app)</span>
              </div>
            ))}
            {!!stats.cancelled && (
              <div style={{ fontSize: 11.5, color: C.slate, fontFamily: "'JetBrains Mono', monospace" }} data-testid="stats-cancelled-note">
                every figure above excludes {stats.cancelled.toLocaleString()} cancelled subscriber{stats.cancelled === 1 ? "" : "s"}
              </div>
            )}
          </div>

          {/* Filters */}
          <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ position: "relative" }}>
              <MagnifyingGlass size={15} style={{ position: "absolute", left: 11, top: 10, color: C.slate }} />
              <input value={f.q} onChange={(e) => { setPage(0); setF({ ...f, q: e.target.value }); }} placeholder="Search name / cell / ID / email / policy…" data-testid="contacts-search"
                style={{ padding: "9px 12px 9px 32px", border: "1px solid rgba(26,30,36,0.14)", borderRadius: 8, fontSize: 13, outline: "none", width: 240 }} />
            </div>
            <Select value={f.product} onChange={(v) => { setPage(0); setF({ ...f, product: v }); }} testid="filter-product" opts={[["", "All products"], ["consumer", "Consumer"], ["commercial", "Commercial"], ["demo", "Demo (test)"]]} />
            <Select value={f.insurer} onChange={(v) => { setPage(0); setF({ ...f, insurer: v }); }} testid="filter-insurer" opts={[["", "All insurers"], ...stats.by_insurer.map((b) => [b.insurer, b.insurer])]} />
            <Select value={f.has_app} onChange={(v) => { setPage(0); setF({ ...f, has_app: v }); }} testid="filter-app" opts={[["", "App: any"], ["true", "Has app"], ["false", "No app"]]} />
            <Select value={f.cancelled} onChange={(v) => { setPage(0); setF({ ...f, cancelled: v }); }} testid="filter-cancelled" opts={[["false", "Active only"], ["true", "Cancelled only"], ["", "Active + cancelled"]]} />
            <Select value={f.activation} onChange={(v) => { setPage(0); setF({ ...f, activation: v }); }} testid="filter-activation" opts={[["", "Activation: any"], ["not_invited", "Not invited yet"], ["invited_not_opened", "Invited · never opened"], ["opened_not_activated", "Opened · not activated"], ["activated", "Activated"]]} />
            <Select value={f.do} onChange={(v) => { setPage(0); setF({ ...f, do: v }); }} testid="filter-do" opts={[["", "Mandate: any"], ["pending", "Mandate: still owed"], ["requested", "Mandate: requested"], ["opened", "Mandate: opened"], ["signed", "Mandate: signed"], ["none", "Mandate: not requested"]]} />
          </div>

          {/* Table */}
          {sel.length > 0 && (
            <div data-testid="selection-bar" style={{ display: "flex", alignItems: "center", gap: 12, background: C.ink, color: "#fff", borderRadius: 10, padding: "10px 14px", marginBottom: 10, fontSize: 13 }}>
              <b data-testid="selection-count">{sel.length} selected</b>
              <span style={{ color: "rgba(255,255,255,0.7)", flex: 1 }}>Welcome pack = one email with their app activation link + debit order form (app users also get the in-app prompt)</span>
              <button onClick={() => setSel([])} data-testid="selection-clear" style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.3)", color: "#fff", borderRadius: 999, padding: "6px 12px", fontSize: 12, cursor: "pointer" }}>Clear</button>
              <button onClick={() => sendPack(sel)} disabled={packBusy} data-testid="selection-send-pack"
                style={{ display: "inline-flex", alignItems: "center", gap: 6, background: C.red, border: 0, color: "#fff", borderRadius: 999, padding: "7px 14px", fontSize: 12.5, fontWeight: 700, cursor: packBusy ? "default" : "pointer", opacity: packBusy ? 0.6 : 1 }}>
                <PaperPlaneTilt size={13} /> {packBusy ? "Sending…" : `Send welcome pack to ${sel.length}`}</button>
            </div>
          )}
          <div style={{ overflowX: "auto", border: "1px solid rgba(26,30,36,0.08)", borderRadius: 12, background: "#fff" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead><tr>
                <th style={{ padding: "12px 8px 12px 16px", borderBottom: "1px solid rgba(26,30,36,0.08)" }}>
                  <input type="checkbox" data-testid="select-all" checked={data.items.length > 0 && data.items.every((c) => sel.includes(c.id))}
                    onChange={(e) => setSel(e.target.checked ? Array.from(new Set([...sel, ...data.items.map((c) => c.id)])) : sel.filter((id) => !data.items.some((c) => c.id === id)))} />
                </th>
                {["Name", "Cell", "ID number", "Insurer", "Policy", "Product", "App", "Activation", "Security", "Link", "Mandate", "Member", ""].map((h) => (
                <th key={h} style={{ textAlign: "left", padding: "12px 16px", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: C.slate, fontFamily: "'JetBrains Mono', monospace", borderBottom: "1px solid rgba(26,30,36,0.08)" }}>{h}</th>
              ))}</tr></thead>
              <tbody>
                {data.items.map((c, i) => (
                  <tr key={c.id} data-testid={`contact-row-${i}`} style={{ background: sel.includes(c.id) ? `${C.navy}08` : undefined }}>
                    <Td style={{ padding: "12px 8px 12px 16px" }}><input type="checkbox" data-testid={`contact-select-${i}`} checked={sel.includes(c.id)} onChange={() => toggleSel(c.id)} /></Td>
                    <Td style={{ fontWeight: 600 }}>
                      <button onClick={() => setMember(c)} data-testid={`contact-open-${i}`}
                        style={{ background: "none", border: 0, padding: 0, color: C.navy, fontWeight: 600, fontSize: 13, cursor: "pointer", textAlign: "left", textDecoration: "underline", textDecorationColor: `${C.navy}44` }}>{c.full_name}</button>
                      {c.email && <div style={{ fontSize: 11, fontWeight: 400, color: C.slate }} data-testid={`contact-email-${i}`}>{c.email}</div>}
                    </Td>
                    <Td mono>{c.cell || "—"}</Td>
                    <Td mono style={{ color: C.slate }}>{c.id_number || "—"}</Td>
                    <Td>{c.insurer}</Td>
                    <Td mono style={{ color: C.slate }} data-testid={`contact-policy-${i}`}>{c.policy_number || "—"}</Td>
                    <Td>{(() => { const [label, color] = planOf(c); return (
                      <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700, color, background: `${color}14`, border: `1px solid ${color}33` }} data-testid={`contact-plan-${i}`}>{label}</span>
                    ); })()}</Td>
                    <Td><span style={{ color: c.has_app ? C.green : C.slate, fontWeight: 700 }}>{c.has_app ? "● Yes" : "○ No"}</span></Td>
                    <Td><ActivationChip c={c} i={i} /></Td>
                    <Td>{c.cancelled ? (
                      <span data-testid={`contact-cancelled-${i}`} title={c.cancel_reason ? `Reason: ${c.cancel_reason}` : undefined}
                        style={{ display: "inline-block", padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700, color: C.red, background: `${C.red}14`, border: `1px solid ${C.red}44` }}>✕ Cancelled</span>
                    ) : c.product === "consumer" ? (
                      <button onClick={() => toggleSecurity(c, i)} data-testid={`contact-security-${i}`}
                        style={{ padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700, cursor: "pointer", color: c.security_active ? C.green : C.slate, background: `${c.security_active ? C.green : C.slate}14`, border: `1px solid ${c.security_active ? C.green : C.slate}33` }}>
                        {c.security_active ? "● Active" : "○ Free"}
                      </button>
                    ) : <span style={{ color: C.slate, fontSize: 12 }}>—</span>}</Td>
                    <Td>{(() => {
                      const LS = { linked: ["Linked", C.green], pending: ["Pending", C.amber], unlinked: ["Unlinked", C.slate] };
                      const s = LS[c.link_status];
                      return s ? (
                        <span data-testid={`contact-link-${i}`} style={{ display: "inline-block", padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700, color: s[1], background: `${s[1]}14`, border: `1px solid ${s[1]}33` }}>{s[0]}</span>
                      ) : <span style={{ color: C.slate, fontSize: 12 }}>—</span>;
                    })()}</Td>
                    <Td style={{ color: C.slate, textTransform: "capitalize" }}>{c.member_type}</Td>
                    <Td>{(() => {
                      const DS = { signed: ["Signed", C.green], opened: ["Opened", C.amber], requested: ["Requested", C.navy] };
                      const s = DS[c.do_status];
                      return s ? (
                        <span data-testid={`contact-do-${i}`} style={{ display: "inline-block", padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700, color: s[1], background: `${s[1]}14`, border: `1px solid ${s[1]}33` }}>{s[0]}</span>
                      ) : <span style={{ color: C.slate, fontSize: 12 }}>—</span>;
                    })()}</Td>
                    <Td><div style={{ display: "flex", gap: 6 }}>
                      <button onClick={() => openHistory(c)} data-testid={`contact-history-${i}`}
                        style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "6px 11px", border: `1px solid ${C.navy}22`, background: `${C.navy}0a`, color: C.navy, borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>
                        <ClockCounterClockwise size={13} /> History</button>
                      <button onClick={() => sendInvite(c)} data-testid={`contact-invite-${i}`}
                        style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "6px 11px", border: `1px solid ${C.red}44`, background: `${C.red}0f`, color: C.red, borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>
                        <PaperPlaneTilt size={13} /> Invite</button>
                      <button onClick={() => sendPack([c.id])} disabled={packBusy} data-testid={`contact-pack-${i}`} title="One email: app activation link + debit order form"
                        style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "6px 11px", border: `1px solid ${C.green}55`, background: `${C.green}10`, color: C.green, borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>
                        <Package size={13} /> Welcome pack</button>
                    </div></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 14, fontSize: 13, color: C.slate }}>
            <span data-testid="contacts-count">Showing {data.total === 0 ? 0 : page * LIMIT + 1}–{Math.min((page + 1) * LIMIT, data.total)} of {data.total.toLocaleString()}</span>
            <div style={{ display: "flex", gap: 8 }}>
              <PageBtn disabled={page === 0} onClick={() => setPage(page - 1)} testid="page-prev">Prev</PageBtn>
              <PageBtn disabled={(page + 1) * LIMIT >= data.total} onClick={() => setPage(page + 1)} testid="page-next">Next</PageBtn>
            </div>
          </div>
        </>
      )}
      {hist && <HistoryModal data={hist} onClose={() => setHist(null)} />}
      {member && <MemberDrawer contact={member} onClose={() => setMember(null)} onSaved={() => { loadStats(); loadContacts(); }} onHistory={(c) => { setMember(null); openHistory(c); }} onInvite={sendInvite}
        onPack={(res) => { setMember(null); setPack([res]); loadContacts(); }} />}
      {pack && <WelcomePackModal results={pack} onClose={() => setPack(null)} />}
      {addOpen && <AddSubscriberModal onClose={() => setAddOpen(false)} onDone={() => { setAddOpen(false); loadStats(); loadContacts(); }} />}
      {invite && <InviteModal invite={invite} onClose={() => setInvite(null)} />}
      {purgeOpen && <PurgePersonModal onClose={() => setPurgeOpen(false)} onDone={() => { loadStats(); loadContacts(); loadUnmatched(); }} />}
      {bulkOpen && <BulkPurgeModal onClose={() => setBulkOpen(false)} onDone={() => { loadStats(); loadContacts(); loadUnmatched(); }} />}
      {reconcileOpen && <ReconcileActiveModal onClose={() => setReconcileOpen(false)} onDone={() => { loadStats(); loadContacts(); loadUnmatched(); }} />}
      {unmatchedOpen && unmatched && <UnmatchedPanel data={unmatched} onClose={() => setUnmatchedOpen(false)} onView={(p) => setHist({
        contact: { full_name: p.full_name, cell: p.cell, insurer: p.insurer },
        count: p.count, counts: { legacy: p.count },
        first_at: (p.items?.[p.items.length - 1] || {}).date_created,
        events: (p.items || []).map((r) => ({
          at: r.date_created, category: "legacy", title: "Help request (old platform)",
          detail: r.comment, who: r.completed_by || r.acknowledged_by,
        })),
      })} onPurge={purgeUnmatched} />}
    </div>
  );
}

function BulkPurgeModal({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [ids, setIds] = useState("");
  const [names, setNames] = useState("");
  const [partner, setPartner] = useState("");
  const [partners, setPartners] = useState([]);
  const [deps, setDeps] = useState(true);
  const [eff, setEff] = useState("");
  const [rep, setRep] = useState(null);
  const [far, setFar] = useState(null);
  const [prog, setProg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/partners").then(({ data }) => setPartners(data.partners || [])).catch(() => {});
  }, []);
  const reset = () => { setRep(null); setFar(null); setProg(null); };

  useEffect(() => {
    if (!prog?.running) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get("/admin/farewell-progress");
        setProg(data);
        if (!data.running) {
          clearInterval(t);
          if (data.sent) toast.success(`Farewell email sent to ${data.sent}`, { description: data.failed ? `${data.failed} could not be sent` : "You can now process the removal" });
          else toast.error("No emails went out", { description: Object.keys(data.reasons || {}).join(", ") || "See the reasons shown in the panel" });
        }
      } catch (e) { /* keep polling */ }
    }, 2500);
    return () => clearInterval(t);
  }, [prog?.running]);

  const body = (confirm) => {
    const fd = new FormData();
    if (file) fd.append("file", file);
    if (ids.trim()) fd.append("ids", ids.trim());
    if (names.trim()) fd.append("names", names.trim());
    if (partner) fd.append("partner", partner);
    fd.append("confirm", confirm ? "true" : "false");
    fd.append("include_dependants", deps ? "true" : "false");
    if (eff.trim()) fd.append("effective_date", eff.trim());
    return fd;
  };

  const run = async (confirm) => {
    if (!file && !ids.trim() && !names.trim()) return toast.error("Choose a file or paste ID numbers or names");
    if (confirm) {
      const n = rep?.will_delete || 0;
      const warn = prog && !prog.running && prog.sent ? "" : "\n\nThe farewell email has NOT been sent from here yet — once these people are deleted we no longer have their email addresses.";
      if (!window.confirm(`PERMANENTLY delete ${n} ${n === 1 ? "person" : "people"} and everything linked to them? This cannot be undone.${warn}`)) return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/bulk-purge", body(confirm), { headers: { "Content-Type": "multipart/form-data" } });
      setRep(data);
      if (confirm) { toast.success(`${data.will_delete} people permanently deleted`); onDone(); }
      else {
        try {
          const { data: f } = await api.post("/admin/farewell-email", body(false), { headers: { "Content-Type": "multipart/form-data" } });
          setFar(f);
        } catch (e) { setFar(null); /* name-only lists carry no email — farewell step is optional */ }
      }
    } catch (e) { toast.error("Failed", { description: e.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };

  const sendFarewell = async () => {
    const n = far?.will_email || 0;
    if (far?.duplicates) {
      const conf = far.duplicates_confirmed_delivered
        ? `\n\n${far.duplicates_confirmed_delivered} of them were CONFIRMED DELIVERED by Resend.` : "";
      if (!window.confirm(`DUPLICATE WARNING\n\n${far.duplicates} of these people have already had this mail in the last 30 days.${conf}\n\nThey have been left out. Press OK to mail only the remaining ${n}.`)) return;
    }
    const gate = far?.email_gate;
    const gateWarn = gate && !gate.can_send_to_everyone
      ? `\n\nWARNING: outbound email is gated in this environment (${!gate.api_key_configured ? "no Resend API key" : "PROD_EMAIL_ENABLED is off"}) — only ${gate.allowlist.join(", ")} will actually receive anything.`
      : "";
    if (!window.confirm(`Send the "sorry to see you go" email to ${n} ${n === 1 ? "person" : "people"} now? This goes out immediately.${gateWarn}`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/farewell-email", body(true), { headers: { "Content-Type": "multipart/form-data" } });
      setFar(data);
      setProg({ running: true, total: data.will_email, sent: 0, failed: 0, reasons: {} });
      toast.success("Sending started", { description: `${data.will_email} emails going out at 2 a second — watch the progress in the panel` });
    } catch (e) { toast.error("Send failed", { description: e.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };

  return (
    <div onClick={onClose} data-testid="bulk-purge-modal" style={{ position: "fixed", inset: 0, background: "rgba(11,30,59,0.45)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 100, padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 620, maxWidth: "95%", maxHeight: "92vh", overflowY: "auto", background: C.body, borderRadius: 14, padding: 24 }}>
        <div style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 22, color: C.ink, marginBottom: 4 }}>Remove a list of people</div>
        <div style={{ fontSize: 12, color: C.slate, marginBottom: 16, lineHeight: 1.5 }}>
          Upload the partner&apos;s spreadsheet (.xlsx or .csv), or paste ID numbers or surnames.
          People are matched on <b>13-digit SA ID</b> where present, otherwise on <b>cell number</b>,
          otherwise on <b>exact name / surname</b>. A name that matches more than one person is
          <b> flagged for review, never deleted</b>. Choose a partner below to scope name/surname
          matching to that book. Preview first; nothing is deleted until you confirm.
          <br /><b>Step 1</b> sends the &quot;sorry to see you go&quot; email (only to people we hold an
          address for), <b>Step 2</b> removes them.
        </div>
        <select value={partner} onChange={(e) => { setPartner(e.target.value); reset(); }} data-testid="bulk-partner"
          style={{ width: "100%", padding: 11, background: "#fff", border: "1px solid rgba(26,30,36,0.16)", borderRadius: 8, fontSize: 13, marginBottom: 10, color: C.ink }}>
          <option value="">All partners (no scope — safer to pick one for name matching)</option>
          {partners.map((p) => <option key={p.name} value={p.name}>{p.name} · {p.count} people</option>)}
        </select>
        <input type="file" accept=".xlsx,.xlsm,.csv,.txt" data-testid="bulk-file"
          onChange={(e) => { setFile(e.target.files[0] || null); reset(); }}
          style={{ width: "100%", padding: 11, background: "#fff", border: "1px solid rgba(26,30,36,0.16)", borderRadius: 8, fontSize: 13, marginBottom: 10 }} />
        <textarea value={ids} onChange={(e) => { setIds(e.target.value); reset(); }} rows={2} data-testid="bulk-ids"
          placeholder="…or paste ID numbers, separated by spaces, commas or new lines"
          style={{ width: "100%", padding: "10px 12px", border: "1px solid rgba(26,30,36,0.16)", borderRadius: 8, fontSize: 13, outline: "none", color: C.ink, resize: "vertical", marginBottom: 10 }} />
        <textarea value={names} onChange={(e) => { setNames(e.target.value); reset(); }} rows={2} data-testid="bulk-names"
          placeholder="…or paste surnames / names, one per line (best used with a partner selected above)"
          style={{ width: "100%", padding: "10px 12px", border: "1px solid rgba(26,30,36,0.16)", borderRadius: 8, fontSize: 13, outline: "none", color: C.ink, resize: "vertical" }} />
        <label style={{ display: "flex", gap: 9, alignItems: "flex-start", marginTop: 12, cursor: "pointer" }}>
          <input type="checkbox" checked={deps} onChange={(e) => { setDeps(e.target.checked); setRep(null); setFar(null); setProg(null); }} data-testid="bulk-deps" style={{ marginTop: 2 }} />
          <span style={{ fontSize: 12.5, color: C.ink, lineHeight: 1.5 }}>
            Also remove dependants sharing those policies — otherwise they stay armed on a policy with no main member
          </span>
        </label>
        <input value={eff} onChange={(e) => { setEff(e.target.value); setFar(null); setProg(null); }} data-testid="farewell-effective-date"
          placeholder="Cover-ended date shown in the farewell email (blank = today)"
          style={{ width: "100%", marginTop: 10, padding: "10px 12px", border: "1px solid rgba(26,30,36,0.16)", borderRadius: 8, fontSize: 13, outline: "none", color: C.ink }} />

        {rep && (
          <div style={{ background: "#fff", border: `1px solid ${rep.dry_run ? C.amber : C.green}55`, borderRadius: 10, padding: 14, margin: "14px 0" }} data-testid="bulk-report">
            <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: C.slate, fontFamily: "'JetBrains Mono', monospace", marginBottom: 10 }}>
              {rep.dry_run ? "Preview — nothing deleted yet" : "Deleted"}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
              {[["ID numbers supplied", rep.supplied, C.navy], ["Matched by ID", rep.matched, C.navy],
                ["Matched by name", rep.matched_by_name || 0, C.navy],
                ["Dependants swept in", rep.include_dependants ? rep.dependants_on_same_policy : 0, C.amber],
                ["Flagged for review", rep.ambiguous_count || 0, C.amber],
                ["Will be deleted", rep.will_delete, C.red],
                ["Not found", (rep.unmatched_count || 0) + (rep.names_not_found_count || 0), C.slate]].map(([l, n, col]) => (
                <div key={l} style={{ flex: "1 1 120px", background: `${col}0f`, border: `1px solid ${col}33`, borderRadius: 8, padding: "8px 10px" }} data-testid={`bulk-${String(l).split(" ")[0].toLowerCase()}`}>
                  <div style={{ fontSize: 9.5, letterSpacing: "0.1em", textTransform: "uppercase", color: C.slate, fontFamily: "'JetBrains Mono', monospace" }}>{l}</div>
                  <div style={{ fontSize: 19, fontWeight: 700, color: col, fontVariantNumeric: "tabular-nums" }}>{n}</div>
                </div>
              ))}
            </div>
            {rep.partner && rep.dry_run && (
              <div style={{ fontSize: 11.5, color: C.slate, marginBottom: 8 }} data-testid="bulk-partner-scope">
                Name / cell matching scoped to partner <b style={{ color: C.ink }}>{rep.partner}</b>.
              </div>
            )}
            {rep.armed > 0 && rep.dry_run && (
              <div style={{ fontSize: 12.5, color: C.red, fontWeight: 600, marginBottom: 8 }} data-testid="bulk-armed-warning">
                {rep.armed} of these people currently have a WORKING panic button{rep.with_app_accounts ? ` and ${rep.with_app_accounts} have a ResQ1 app account` : ""} — deleting them disarms them immediately.
              </div>
            )}
            {(rep.sample || []).length > 0 && (
              <div style={{ maxHeight: 190, overflowY: "auto", borderTop: `1px solid ${C.ink}12`, paddingTop: 8 }}>
                {rep.sample.map((p, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "4px 0", fontSize: 12.5 }} data-testid={`bulk-sample-${i}`}>
                    <span style={{ color: C.ink, fontWeight: 600 }}>{p.name}{p.armed ? <span style={{ color: C.green, fontWeight: 400 }}> · armed</span> : null}</span>
                    <span style={{ color: C.slate, fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>{p.policy || "no policy"} · {p.why}</span>
                  </div>
                ))}
                {rep.will_delete > rep.sample.length && (
                  <div style={{ fontSize: 11.5, color: C.slate, marginTop: 6 }}>…and {rep.will_delete - rep.sample.length} more</div>
                )}
              </div>
            )}
            {(rep.ambiguous || []).length > 0 && rep.dry_run && (
              <div style={{ fontSize: 11.5, color: C.amber, marginTop: 10, lineHeight: 1.6, background: `${C.amber}0f`, border: `1px solid ${C.amber}44`, borderRadius: 8, padding: "9px 11px" }} data-testid="bulk-ambiguous">
                <b style={{ color: C.ink }}>Flagged for review — NOT deleted</b> (each matches more than one person, so we can&apos;t be sure which):
                <div style={{ marginTop: 4 }}>{rep.ambiguous.map((a) => `${a.label} (${a.count})`).join(", ")}</div>
                Narrow these by selecting the partner above, or remove them one-by-one with &quot;Start a person from scratch&quot;.
              </div>
            )}
            {(rep.names_not_found || []).length > 0 && rep.dry_run && (
              <div style={{ fontSize: 11.5, color: C.slate, marginTop: 10, lineHeight: 1.5 }} data-testid="bulk-names-not-found">
                Not found{rep.partner ? ` under ${rep.partner}` : ""}: {rep.names_not_found.join(", ")}.
              </div>
            )}
            {(rep.rows_without_id || []).length > 0 && rep.matched_by_name === 0 && rep.dry_run && (
              <div style={{ fontSize: 11.5, color: C.slate, marginTop: 10, lineHeight: 1.5 }} data-testid="bulk-no-id-rows">
                Rows with no ID: {rep.rows_without_id.join(", ")}.
              </div>
            )}
          </div>
        )}

        {far && (
          <div style={{ background: "#fff", border: `1px solid ${prog ? (prog.running ? C.amber : C.green) : C.navy}55`, borderRadius: 10, padding: 14, margin: "14px 0" }} data-testid="farewell-panel">
            <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: C.slate, fontFamily: "'JetBrains Mono', monospace", marginBottom: 8 }}>
              Step 1 · &quot;Sorry to see you go&quot; email
            </div>
            {!prog ? (
              <>
                {far.email_gate && !far.email_gate.can_send_to_everyone && (
                  <div style={{ fontSize: 12.5, color: C.red, fontWeight: 600, lineHeight: 1.6, marginBottom: 10, background: `${C.red}0f`, border: `1px solid ${C.red}33`, borderRadius: 8, padding: "9px 11px" }} data-testid="farewell-gate-warning">
                    Outbound email is switched OFF in this environment
                    ({!far.email_gate.api_key_configured ? "no Resend API key configured" : "live email is held"}).
                    Only {far.email_gate.allowlist.join(", ")} would receive anything — everyone else would be blocked.
                    {far.email_gate.api_key_configured ? " Turn it on in Settings → \"Go live with email\", then preview again." : ""}
                  </div>
                )}
                <div style={{ fontSize: 13, color: C.ink, lineHeight: 1.6, marginBottom: 8 }} data-testid="farewell-count">
                  <b>{far.will_email}</b> of these people have a usable email address and will receive the farewell
                  email (cover ended {far.effective_date}, POPIA deletion notice, a &quot;keep your cover from R29pm&quot;
                  link, and the note to mail Genevieve@resq1.co.za if it was cancelled in error).
                </div>
                <div style={{ fontSize: 11.5, color: C.slate, lineHeight: 1.6 }} data-testid="farewell-skipped">
                  Skipped: {far.skipped.no_email} with no email anywhere · {far.skipped.invalid_email || 0} with an
                  unusable address on file · {far.skipped.undeliverable} dead addresses ·
                  {" "}{far.skipped.opted_out} opted out · {far.skipped.already_sent} already mailed.
                </div>
                {far.recovered_emails > 0 && (
                  <div style={{ fontSize: 11.5, color: C.navy, lineHeight: 1.6, marginTop: 6 }} data-testid="farewell-recovered">
                    Cross-referenced <b>{far.recovered_emails}</b> address{far.recovered_emails === 1 ? "" : "es"} that
                    were missing on the subscriber record — {Object.entries(far.email_sources || {}).filter(([s]) => s !== "subscriber record").map(([s, n]) => `${n} from their ${s}`).join(" · ")}.
                  </div>
                )}
                {far.duplicates > 0 && (
                  <div style={{ fontSize: 12.5, color: C.red, fontWeight: 600, lineHeight: 1.55, marginTop: 8, background: `${C.red}0f`, border: `1px solid ${C.red}33`, borderRadius: 8, padding: "9px 11px" }} data-testid="farewell-duplicates">
                    {far.duplicate_warning}
                  </div>
                )}
                {(far.sample || []).length > 0 && (
                  <div style={{ maxHeight: 130, overflowY: "auto", borderTop: `1px solid ${C.ink}12`, marginTop: 8, paddingTop: 8 }}>
                    {far.sample.map((p, i) => (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "3px 0", fontSize: 12 }} data-testid={`farewell-sample-${i}`}>
                        <span style={{ color: C.ink, fontWeight: 600 }}>{p.name}</span>
                        <span style={{ color: C.slate, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{p.email}{p.source && p.source !== "subscriber record" ? ` · via ${p.source}` : ""}</span>
                      </div>
                    ))}
                  </div>
                )}
                <button onClick={sendFarewell} disabled={busy || !far.will_email} data-testid="farewell-send-btn"
                  style={{ marginTop: 12, width: "100%", padding: 12, background: C.navy, color: "#fff", border: 0, borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: busy || !far.will_email ? "default" : "pointer", opacity: busy || !far.will_email ? 0.45 : 1 }}>
                  {busy ? "Sending…" : `Send the farewell email to ${far.will_email}`}
                </button>
              </>
            ) : (
              <div data-testid="farewell-progress">
                <div style={{ fontSize: 13, color: C.ink, lineHeight: 1.6 }}>
                  {prog.running
                    ? <>Sending… <b>{prog.sent}</b> of {prog.total} delivered to Resend{prog.failed ? ` · ${prog.failed} failed` : ""}</>
                    : <>Finished — sent to <b>{prog.sent}</b> of {prog.total}{prog.failed ? ` · ${prog.failed} could not be sent` : ""}. {prog.sent ? "You can now process the removal below." : ""}</>}
                </div>
                <div style={{ height: 6, background: `${C.ink}12`, borderRadius: 4, marginTop: 8, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${Math.round(((prog.sent + prog.failed) / Math.max(prog.total, 1)) * 100)}%`, background: prog.running ? C.amber : C.green, transition: "width 400ms" }} />
                </div>
                {Object.keys(prog.reasons || {}).length > 0 && (
                  <div style={{ fontSize: 12, color: C.red, lineHeight: 1.6, marginTop: 10 }} data-testid="farewell-reasons">
                    Why they failed: {Object.entries(prog.reasons).map(([r, n]) => `${n} × ${r === "blocked" ? "blocked by the email safety gate (PROD_EMAIL_ENABLED / allowlist)" : r === "simulated" ? "no Resend API key in this environment" : r}`).join(" · ")}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <button onClick={onClose} style={{ flex: 1, padding: 12, background: "transparent", border: `1px solid ${C.slate}44`, color: C.slate, borderRadius: 8, fontSize: 13, cursor: "pointer" }}>Close</button>
          <button onClick={() => run(false)} disabled={busy} data-testid="bulk-preview-btn"
            style={{ flex: 1, padding: 12, background: C.ink, color: "#fff", border: 0, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1 }}>
            {busy ? "Checking…" : "Preview"}
          </button>
          <button onClick={() => run(true)} disabled={busy || !rep?.dry_run || !rep?.will_delete} data-testid="bulk-confirm-btn"
            style={{ flex: 1.5, padding: 12, background: C.red, color: "#fff", border: 0, borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: busy || !rep?.dry_run || !rep?.will_delete ? "default" : "pointer", opacity: busy || !rep?.dry_run || !rep?.will_delete ? 0.45 : 1 }}>
            Delete {rep?.will_delete || ""} permanently
          </button>
        </div>
      </div>
    </div>
  );
}

function ReconcileActiveModal({ onClose, onDone }) {
  const [partners, setPartners] = useState([]);
  const [partner, setPartner] = useState("");
  const [file, setFile] = useState(null);
  const [deps, setDeps] = useState(true);
  const [force, setForce] = useState(false);
  const [rep, setRep] = useState(null);
  const [keep, setKeep] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [addRep, setAddRep] = useState(null);

  const loadPartners = () => api.get("/admin/partners").then(({ data }) => setPartners(data.partners || [])).catch(() => {});
  useEffect(() => {
    loadPartners();
  }, []);
  const reset = () => { setRep(null); setForce(false); setKeep(new Set()); setAddRep(null); };
  const toggleKeep = (id) => setKeep((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const selectedCount = partners.find((p) => p.name === partner)?.count;
  const isEmptyPartner = !!partner && selectedCount === 0;

  const body = (confirm) => {
    const fd = new FormData();
    if (file) fd.append("file", file);
    fd.append("partner", partner);
    fd.append("confirm", confirm ? "true" : "false");
    fd.append("include_dependants", deps ? "true" : "false");
    fd.append("force", force ? "true" : "false");
    if (keep.size) fd.append("keep_ids", Array.from(keep).join(","));
    return fd;
  };

  const adjDelete = rep ? Math.max(0, rep.will_delete - keep.size) : 0;
  const adjFraction = rep && rep.partner_total ? adjDelete / rep.partner_total : 0;
  const effBlocked = rep ? (adjDelete > 0 && adjFraction * 100 > rep.threshold_pct) : false;

  const run = async (confirm) => {
    if (!partner) return toast.error("Choose the partner this active list belongs to");
    if (!file) return toast.error("Upload the partner's active list");
    if (confirm) {
      const n = adjDelete;
      if (!window.confirm(`PERMANENTLY delete ${n} ${partner} ${n === 1 ? "person" : "people"} who are NOT on this active list, and everything linked to them? No email is sent. This cannot be undone.`)) return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reconcile-active", body(confirm), { headers: { "Content-Type": "multipart/form-data" } });
      if (confirm) { toast.success(`${data.will_delete} people permanently deleted`); onDone(); setRep(data); }
      else { setRep(data); setKeep(new Set((data.suspected || []).map((s) => s.id))); }
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error(e.response?.status === 409 ? "Safety stop" : "Failed", { description: detail });
    } finally { setBusy(false); }
  };

  const addMissing = async () => {
    const fd = new FormData();
    if (file) fd.append("file", file);
    fd.append("partner", partner);
    setBusy(true);
    try {
      const { data: dry } = await api.post("/admin/reconcile-active/add-missing", (() => { const f = new FormData(); if (file) f.append("file", file); f.append("partner", partner); f.append("confirm", "false"); return f; })(), { headers: { "Content-Type": "multipart/form-data" } });
      if (!dry.will_add) { toast.info("Nothing to add — everyone on the list is already in your book"); setBusy(false); return; }
      if (!window.confirm(`Add ${dry.will_add} ${partner} ${dry.will_add === 1 ? "person" : "people"} from the active list into your book, as active members?`)) { setBusy(false); return; }
      fd.append("confirm", "true");
      const { data } = await api.post("/admin/reconcile-active/add-missing", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.added} people added to ${partner}`);
      onDone();
      run(false);
    } catch (e) {
      toast.error("Failed to add", { description: e.response?.data?.detail || e.message });
      setBusy(false);
    }
  };

  const previewAdd = async () => {
    if (!partner) return toast.error("Choose the partner this list belongs to");
    if (!file) return toast.error("Upload the list you want to add");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("partner", partner); fd.append("confirm", "false");
      const { data } = await api.post("/admin/reconcile-active/add-missing", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAddRep(data);
      if (!data.will_add && !data.will_move) toast.info("Nothing to do — everyone on the list is already on this partner");
    } catch (e) {
      toast.error("Couldn't read that list", { description: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const confirmAdd = async () => {
    if (!addRep) return;
    const total = (addRep.will_add || 0) + (addRep.will_move || 0);
    if (!total) return;
    if (!window.confirm(`Add ${addRep.will_add} new and move ${addRep.will_move} existing ${total === 1 ? "person" : "people"} into ${partner}, all with emergency cover active? This cannot be undone automatically.`)) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("partner", partner); fd.append("confirm", "true");
      const { data } = await api.post("/admin/reconcile-active/add-missing", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.added} added · ${data.moved} moved into ${partner}`);
      setAddRep({ ...data });
      await loadPartners();
      onDone();
    } catch (e) {
      toast.error("Failed to add & reconcile", { description: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const tiles = rep ? [
    [`${rep.partner} on file`, rep.partner_total, C.navy],
    ["On the active list (kept)", rep.on_active_list + keep.size, C.green],
    ["Will be deleted", adjDelete, C.red],
    ["Rescued (kept active)", keep.size, C.amber],
  ] : [];

  return (
    <div onClick={onClose} data-testid="reconcile-active-modal" style={{ position: "fixed", inset: 0, background: "rgba(11,30,59,0.45)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 100, padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 620, maxWidth: "95%", maxHeight: "92vh", overflowY: "auto", background: C.body, borderRadius: 14, padding: 24 }}>
        <div style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 22, color: C.ink, marginBottom: 4 }}>Reconcile an active list</div>
        <div style={{ fontSize: 12, color: C.slate, marginBottom: 16, lineHeight: 1.5 }}>
          Upload the list of people a partner tells you are <b>still active</b>. Everyone on that
          partner&apos;s book who is <b>NOT</b> on the list is permanently deleted — <b>no email is sent</b>.
          Matching is on ID, else cell, else surname; a policy is kept if <b>any</b> member appears on
          the list. Preview first; a run that would remove more than {rep?.threshold_pct || 40}% of the
          partner is blocked unless you override.
        </div>
        <select value={partner} onChange={(e) => { setPartner(e.target.value); reset(); }} data-testid="reconcile-partner"
          style={{ width: "100%", padding: 11, background: "#fff", border: "1px solid rgba(26,30,36,0.16)", borderRadius: 8, fontSize: 13, marginBottom: 10, color: C.ink }}>
          <option value="">Choose the partner this list belongs to…</option>
          {partners.map((p) => <option key={p.name} value={p.name}>{p.name} · {p.count} people</option>)}
        </select>
        <input type="file" accept=".xlsx,.xlsm,.csv,.txt" data-testid="reconcile-file"
          onChange={(e) => { setFile(e.target.files[0] || null); reset(); }}
          style={{ width: "100%", padding: 11, background: "#fff", border: "1px solid rgba(26,30,36,0.16)", borderRadius: 8, fontSize: 13, marginBottom: 10 }} />
        <label style={{ display: "flex", gap: 9, alignItems: "flex-start", cursor: isEmptyPartner ? "default" : "pointer", opacity: isEmptyPartner ? 0.4 : 1 }}>
          <input type="checkbox" checked={deps} disabled={isEmptyPartner} onChange={(e) => { setDeps(e.target.checked); reset(); }} data-testid="reconcile-deps" style={{ marginTop: 2 }} />
          <span style={{ fontSize: 12.5, color: C.ink, lineHeight: 1.5 }}>
            Delete the whole policy — dependants of a lapsed main go too
          </span>
        </label>

        {isEmptyPartner && (
          <div style={{ background: `${C.navy}08`, border: `1px solid ${C.navy}33`, borderRadius: 10, padding: 14, margin: "14px 0" }} data-testid="reconcile-empty-note">
            <div style={{ fontSize: 12.5, color: C.ink, fontWeight: 600, marginBottom: 2 }}>
              {partner} has no members yet — this will ADD a new list
            </div>
            <div style={{ fontSize: 11.5, color: C.slate, lineHeight: 1.55 }}>
              Everyone on the file is added to {partner} with emergency cover <b>active</b>. Anyone who
              already exists under another partner is <b>moved</b> here (never duplicated). Nothing is deleted.
            </div>
            {addRep && (
              <div style={{ background: "#fff", border: `1px solid ${addRep.dry_run ? C.amber : C.green}55`, borderRadius: 10, padding: 14, marginTop: 12 }} data-testid="reconcile-add-report">
                <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: C.slate, fontFamily: "'JetBrains Mono', monospace", marginBottom: 10 }}>
                  {addRep.dry_run ? "Preview — nothing saved yet" : "Done"}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
                  {[["On the list", addRep.list_total, C.navy],
                    [addRep.dry_run ? "Will add (new)" : "Added", addRep.dry_run ? addRep.will_add : addRep.added, C.green],
                    [addRep.dry_run ? "Will move here" : "Moved here", addRep.dry_run ? addRep.will_move : addRep.moved, C.amber],
                    ["Already here", addRep.already_here, C.slate]].map(([l, n, col]) => (
                    <div key={l} style={{ flex: "1 1 120px", background: `${col}0f`, border: `1px solid ${col}33`, borderRadius: 8, padding: "8px 10px" }} data-testid={`reconcile-add-${String(l).split(" ")[0].toLowerCase()}`}>
                      <div style={{ fontSize: 9.5, letterSpacing: "0.1em", textTransform: "uppercase", color: C.slate, fontFamily: "'JetBrains Mono', monospace" }}>{l}</div>
                      <div style={{ fontSize: 19, fontWeight: 700, color: col, fontVariantNumeric: "tabular-nums" }}>{n ?? 0}</div>
                    </div>
                  ))}
                </div>
                {(addRep.move_sample || []).length > 0 && (
                  <div style={{ borderTop: `1px solid ${C.ink}12`, paddingTop: 8, marginBottom: 6 }}>
                    <div style={{ fontSize: 10.5, color: C.slate, marginBottom: 4 }}>Moved from another partner:</div>
                    {addRep.move_sample.map((p, i) => (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "3px 0", fontSize: 12 }} data-testid={`reconcile-move-${i}`}>
                        <span style={{ color: C.ink, fontWeight: 600 }}>{p.name}</span>
                        <span style={{ color: C.amber, fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>was: {p.from}</span>
                      </div>
                    ))}
                  </div>
                )}
                {(addRep.sample || []).length > 0 && (
                  <div style={{ borderTop: `1px solid ${C.ink}12`, paddingTop: 8 }}>
                    <div style={{ fontSize: 10.5, color: C.slate, marginBottom: 4 }}>New people added:</div>
                    {addRep.sample.map((p, i) => (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "3px 0", fontSize: 12 }} data-testid={`reconcile-add-sample-${i}`}>
                        <span style={{ color: C.ink, fontWeight: 600 }}>{p.name}</span>
                        <span style={{ color: C.slate, fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>{p.cell || p.id || "—"} · {p.plan || "single"}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {rep && (
          <div style={{ background: "#fff", border: `1px solid ${rep.dry_run ? (rep.blocked ? C.red : C.amber) : C.green}55`, borderRadius: 10, padding: 14, margin: "14px 0" }} data-testid="reconcile-report">
            <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: C.slate, fontFamily: "'JetBrains Mono', monospace", marginBottom: 10 }}>
              {rep.dry_run ? "Preview — nothing deleted yet" : "Deleted"}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
              {tiles.map(([l, n, col]) => (
                <div key={l} style={{ flex: "1 1 130px", background: `${col}0f`, border: `1px solid ${col}33`, borderRadius: 8, padding: "8px 10px" }} data-testid={`reconcile-${String(l).split(" ")[0].toLowerCase()}`}>
                  <div style={{ fontSize: 9.5, letterSpacing: "0.1em", textTransform: "uppercase", color: C.slate, fontFamily: "'JetBrains Mono', monospace" }}>{l}</div>
                  <div style={{ fontSize: 19, fontWeight: 700, color: col, fontVariantNumeric: "tabular-nums" }}>{n}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 11.5, color: C.slate, marginBottom: 8 }} data-testid="reconcile-list-summary">
              Active list read: {rep.list_ids} ID numbers · {rep.list_cells} cell numbers · {rep.list_surnames} names/surnames.
              That removes <b style={{ color: effBlocked ? C.red : C.ink }}>{Math.round(adjFraction * 100)}%</b> of {rep.partner}.
            </div>
            {(rep.suspected || []).length > 0 && rep.dry_run && (
              <div style={{ border: `1px solid ${C.amber}55`, background: `${C.amber}0c`, borderRadius: 8, padding: "10px 12px", marginBottom: 10 }} data-testid="reconcile-suspected-panel">
                <div style={{ fontSize: 12.5, color: C.ink, fontWeight: 600, marginBottom: 2 }}>
                  {rep.suspected.length} suspected still-active — tick to keep
                </div>
                <div style={{ fontSize: 11, color: C.slate, marginBottom: 8, lineHeight: 1.5 }}>
                  These are due for deletion but LOOSELY match someone on the active list (by name or cell) — likely the same people whose ID didn&apos;t line up. Ticked people are kept active and NOT deleted.
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <button onClick={() => setKeep(new Set(rep.suspected.map((s) => s.id)))} data-testid="reconcile-keep-all" style={{ fontSize: 11, color: C.navy, background: "none", border: 0, cursor: "pointer", fontWeight: 600 }}>Keep all</button>
                  <button onClick={() => setKeep(new Set())} data-testid="reconcile-keep-none" style={{ fontSize: 11, color: C.slate, background: "none", border: 0, cursor: "pointer", fontWeight: 600 }}>Keep none</button>
                </div>
                <div style={{ maxHeight: 200, overflowY: "auto" }}>
                  {rep.suspected.map((s, i) => (
                    <label key={s.id} data-testid={`reconcile-suspected-${i}`} style={{ display: "flex", gap: 9, alignItems: "flex-start", padding: "5px 0", borderTop: i ? `1px solid ${C.ink}0f` : 0, cursor: "pointer" }}>
                      <input type="checkbox" checked={keep.has(s.id)} onChange={() => toggleKeep(s.id)} data-testid={`reconcile-keep-${i}`} style={{ marginTop: 3 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12.5, color: C.ink, fontWeight: 600 }}>{s.name}{s.armed ? <span style={{ color: C.green, fontWeight: 400 }}> · armed</span> : null}</div>
                        <div style={{ fontSize: 10.5, color: C.slate, fontFamily: "'JetBrains Mono', monospace" }}>
                          matches by {s.reason}{s.resembles ? ` · "${s.resembles}"` : ""}{s.cell ? ` · ${s.cell}` : ""} · {s.policy || "no policy"}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}
            {(rep.not_in_book || []).length > 0 && rep.dry_run && (
              <div style={{ border: `1px solid ${C.navy}33`, background: `${C.navy}08`, borderRadius: 8, padding: "10px 12px", marginBottom: 10 }} data-testid="reconcile-not-in-book">
                <div style={{ fontSize: 12.5, color: C.ink, fontWeight: 600, marginBottom: 2 }}>
                  {rep.not_in_book_count} on the active list are NOT in your {rep.partner} book
                </div>
                <div style={{ fontSize: 11, color: C.slate, marginBottom: 6, lineHeight: 1.5 }}>
                  We can&apos;t find these people by ID, cell or surname — they won&apos;t be deleted (they&apos;re not here), but you may need to ADD them.
                </div>
                <div style={{ fontSize: 11.5, color: C.navy, fontFamily: "'JetBrains Mono', monospace", lineHeight: 1.6, maxHeight: 120, overflowY: "auto" }}>
                  {rep.not_in_book.join(" · ")}
                </div>
                <button onClick={addMissing} disabled={busy} data-testid="reconcile-add-missing-btn"
                  style={{ marginTop: 10, padding: "9px 16px", background: C.navy, color: "#fff", border: 0, borderRadius: 999, fontSize: 12.5, fontWeight: 600, cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1 }}>
                  + Add the missing people to {rep.partner}
                </button>
              </div>
            )}
            {effBlocked && rep.dry_run && (
              <div style={{ fontSize: 12.5, color: C.red, fontWeight: 600, lineHeight: 1.55, background: `${C.red}0f`, border: `1px solid ${C.red}44`, borderRadius: 8, padding: "9px 11px", marginBottom: 8 }} data-testid="reconcile-blocked">
                Safety stop — this would delete more than {rep.threshold_pct}% of {rep.partner}. Check the file is the ACTIVE list (not a cancellation list), and rescue any suspected people above. To proceed anyway, tick the override below.
                <label style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8, cursor: "pointer", fontWeight: 500, color: C.ink }}>
                  <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} data-testid="reconcile-force" />
                  I have checked the file — delete {adjDelete} people anyway
                </label>
              </div>
            )}
            {rep.armed > 0 && rep.dry_run && (
              <div style={{ fontSize: 12.5, color: C.red, fontWeight: 600, marginBottom: 8 }} data-testid="reconcile-armed-warning">
                {rep.armed} of these have a WORKING panic button{rep.with_app_accounts ? ` and ${rep.with_app_accounts} have an app account` : ""} — deleting them disarms them immediately.
              </div>
            )}
            {(rep.sample || []).length > 0 && (
              <div style={{ maxHeight: 180, overflowY: "auto", borderTop: `1px solid ${C.ink}12`, paddingTop: 8 }}>
                <div style={{ fontSize: 10.5, color: C.slate, marginBottom: 4 }}>To be deleted (not on the list):</div>
                {rep.sample.map((p, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "4px 0", fontSize: 12.5 }} data-testid={`reconcile-sample-${i}`}>
                    <span style={{ color: C.ink, fontWeight: 600 }}>{p.name}{p.armed ? <span style={{ color: C.green, fontWeight: 400 }}> · armed</span> : null}</span>
                    <span style={{ color: C.slate, fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>{p.policy || "no policy"} · {p.member_type || "main"}</span>
                  </div>
                ))}
                {rep.will_delete > rep.sample.length && (
                  <div style={{ fontSize: 11.5, color: C.slate, marginTop: 6 }}>…and more</div>
                )}
              </div>
            )}
          </div>
        )}

        <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
          <button onClick={onClose} data-testid="reconcile-close-btn"
            style={{ padding: "11px 18px", background: "transparent", color: C.slate, border: `1px solid ${C.slate}44`, borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Close</button>
          {isEmptyPartner ? (
            <>
              <button onClick={previewAdd} disabled={busy || !file} data-testid="reconcile-add-preview-btn"
                style={{ padding: "11px 18px", background: C.navy, color: "#fff", border: 0, borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: (busy || !file) ? "default" : "pointer", opacity: (busy || !file) ? 0.5 : 1 }}>
                {busy ? "Reading…" : "Preview"}</button>
              <button onClick={confirmAdd} disabled={busy || !addRep?.dry_run || !((addRep?.will_add || 0) + (addRep?.will_move || 0))} data-testid="reconcile-add-confirm-btn"
                style={{ padding: "11px 18px", background: C.green, color: "#fff", border: 0, borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: (busy || !addRep?.dry_run || !((addRep?.will_add || 0) + (addRep?.will_move || 0))) ? 0.5 : 1 }}>
                {addRep?.dry_run && ((addRep?.will_add || 0) + (addRep?.will_move || 0)) ? `Add & reconcile ${(addRep.will_add || 0) + (addRep.will_move || 0)}` : "Add and reconcile"}</button>
            </>
          ) : (
            <>
              <button onClick={() => run(false)} disabled={busy} data-testid="reconcile-preview-btn"
                style={{ padding: "11px 18px", background: C.navy, color: "#fff", border: 0, borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1 }}>
                {busy ? "Checking…" : "Preview"}</button>
              <button onClick={() => run(true)} disabled={busy || !rep?.dry_run || !adjDelete || (effBlocked && !force)} data-testid="reconcile-confirm-btn"
                style={{ padding: "11px 18px", background: C.red, color: "#fff", border: 0, borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: (busy || !rep?.dry_run || !adjDelete || (effBlocked && !force)) ? 0.5 : 1 }}>
                {adjDelete ? `Delete ${adjDelete} not on the list` : "Delete"}</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


function PurgePersonModal({ onClose, onDone }) {
  const [v, setV] = useState({ emails: "", cells: "" });
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const payload = (confirm) => ({
    emails: v.emails.split(/[,\s]+/).filter(Boolean),
    cells: v.cells.split(/[,\s]+/).filter(Boolean),
    confirm,
  });
  const run = async (confirm) => {
    const p = payload(confirm);
    if (!p.emails.length && !p.cells.length) return toast.error("Enter an email or cell number");
    if (confirm && !window.confirm("This permanently deletes every record for these people. Continue?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/purge-person", p);
      setReport(data);
      if (confirm) { toast.success("Purged — they can now be onboarded from scratch"); onDone(); }
    } catch (e) { toast.error("Purge failed", { description: e.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };
  const counts = report && (report.deleted || report.would_delete || {});
  const found = report?.found || {};
  const nothing = report && Object.keys(counts).length === 0;
  return (
    <div onClick={onClose} data-testid="purge-person-modal" style={{ position: "fixed", inset: 0, background: "rgba(11,30,59,0.45)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 100, padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 520, maxWidth: "94%", maxHeight: "90vh", overflowY: "auto", background: C.body, borderRadius: 14, padding: 24 }}>
        <div style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 22, color: C.ink, marginBottom: 4 }}>Start a person from scratch</div>
        <div style={{ fontSize: 12, color: C.slate, marginBottom: 16, lineHeight: 1.5 }}>
          Deletes every record for these people — app account, subscriber record, household, subscriptions and mandate audit, invites, incidents, legacy help history and login/reset tokens. Preview first, then purge.
        </div>
        {[["emails", "Email address(es)", "name@example.co.za, other@example.co.za"], ["cells", "Cell number(s) (optional)", "072 555 0143"]].map(([k, label, ph]) => (
          <div key={k} style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: C.slate, marginBottom: 5 }}>{label}</div>
            <input value={v[k]} onChange={(e) => { setV({ ...v, [k]: e.target.value }); setReport(null); }} placeholder={ph} data-testid={`purge-${k}`}
              style={{ width: "100%", padding: "10px 12px", border: "1px solid rgba(26,30,36,0.16)", borderRadius: 8, fontSize: 13, outline: "none", color: C.ink }} />
          </div>
        ))}
        {report && (
          <div style={{ background: "#fff", border: `1px solid ${nothing ? C.green : C.amber}55`, borderRadius: 10, padding: 14, margin: "6px 0 14px" }} data-testid="purge-report">
            <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: C.slate, fontFamily: "'JetBrains Mono', monospace", marginBottom: 8 }}>
              {report.dry_run ? "Preview — nothing deleted yet" : "Deleted"}
            </div>
            {nothing ? (
              <div style={{ fontSize: 13, color: C.green, fontWeight: 600 }} data-testid="purge-clean">Nothing found — this person is already clean.</div>
            ) : (
              <>
                {(found.users || []).map((u) => (
                  <div key={u.email} style={{ fontSize: 12.5, color: C.ink, marginBottom: 4 }}>
                    App/console account <b>{u.email}</b> · role <b style={{ color: u.role === "admin" ? C.red : C.navy }}>{u.role}</b>
                  </div>
                ))}
                {(found.contacts || []).map((c) => (
                  <div key={c.cell} style={{ fontSize: 12.5, color: C.ink, marginBottom: 4 }}>Subscriber record <b>{c.full_name}</b> · {c.cell}</div>
                ))}
                {(found.subscriptions || []).map((s) => (
                  <div key={s.ref} style={{ fontSize: 12.5, color: C.ink, marginBottom: 4 }}>Subscription <b>{s.ref}</b> · {s.status}</div>
                ))}
                <div style={{ borderTop: `1px solid ${C.ink}12`, marginTop: 8, paddingTop: 8, display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {Object.entries(counts).map(([k, n]) => (
                    <span key={k} style={{ fontSize: 11.5, background: C.cream, borderRadius: 999, padding: "4px 10px", color: C.ink, fontFamily: "'JetBrains Mono', monospace" }} data-testid={`purge-count-${k}`}>{k}: {n}</span>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={onClose} style={{ flex: 1, padding: 12, background: "transparent", border: `1px solid ${C.slate}44`, color: C.slate, borderRadius: 8, fontSize: 13, cursor: "pointer" }}>Close</button>
          <button onClick={() => run(false)} disabled={busy} data-testid="purge-preview-btn"
            style={{ flex: 1, padding: 12, background: C.ink, color: "#fff", border: 0, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1 }}>Preview</button>
          <button onClick={() => run(true)} disabled={busy || !report || nothing} data-testid="purge-confirm-btn"
            style={{ flex: 1.4, padding: 12, background: C.red, color: "#fff", border: 0, borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: busy || !report || nothing ? "default" : "pointer", opacity: busy || !report || nothing ? 0.45 : 1 }}>
            {busy ? "Working…" : "Purge everything"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AddSubscriberModal({ onClose, onDone }) {
  const [v, setV] = useState({ full_name: "", cell: "", id_number: "", email: "", product: "consumer", plan: "", policy_number: "", insurer: "", legacy_app_user: false });
  const [busy, setBusy] = useState(false);
  const [partners, setPartners] = useState([]);
  useEffect(() => { api.get("/admin/partners").then(({ data }) => setPartners(data.partners || [])).catch(() => {}); }, []);
  const save = async () => {
    if (!v.full_name.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      const { data: r } = await api.post("/contacts", { ...v });
      toast.success("Subscriber added", {
        description: r.invite_sent
          ? "Activation email sent automatically"
          : (v.email ? "No email sent" : "No email on file — add one to send the activation link"),
      });
      onDone();
    }
    catch (e) { toast.error("Couldn't add subscriber", { description: e.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };
  const fld = (k, label, ph) => (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, color: C.slate, marginBottom: 5 }}>{label}</div>
      <input value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} placeholder={ph} data-testid={`add-sub-${k}`}
        style={{ width: "100%", padding: "10px 12px", border: "1px solid rgba(26,30,36,0.16)", borderRadius: 8, fontSize: 13, outline: "none", color: C.ink }} />
    </div>
  );
  return (
    <div onClick={onClose} data-testid="add-subscriber-modal" style={{ position: "fixed", inset: 0, background: "rgba(11,30,59,0.45)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 100 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 420, maxWidth: "92%", maxHeight: "90vh", overflowY: "auto", background: C.body, borderRadius: 14, padding: 24 }}>
        <div style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 22, color: C.ink, marginBottom: 4 }}>Add subscriber</div>
        <div style={{ fontSize: 12, color: C.slate, marginBottom: 16 }}>Create a subscriber you can invite — a demo account to trial the app, or a legacy member for the migration wave.</div>
        {fld("full_name", "Full name", "Jane Doe")}
        {fld("cell", "Cell number", "27821234567")}
        {fld("id_number", "ID number (optional)")}
        {fld("email", "Email (optional)")}
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: C.slate, marginBottom: 5 }}>Insurer / partner label (optional)</div>
          <input value={v.insurer} onChange={(e) => setV({ ...v, insurer: e.target.value })} placeholder="ResQ1" list="partner-options" data-testid="add-sub-insurer"
            style={{ width: "100%", padding: "10px 12px", border: "1px solid rgba(26,30,36,0.16)", borderRadius: 8, fontSize: 13, outline: "none", color: C.ink }} />
          <datalist id="partner-options">
            {partners.map((p) => <option key={p.name} value={p.name} />)}
          </datalist>
        </div>
        {fld("policy_number", "Policy number (optional)", "Links household members together")}
        <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: C.slate, marginBottom: 5 }}>Product</div>
            <select value={v.product} onChange={(e) => setV({ ...v, product: e.target.value })} data-testid="add-sub-product"
              style={{ width: "100%", padding: "10px 12px", border: "1px solid rgba(26,30,36,0.16)", borderRadius: 8, fontSize: 13, background: "#fff", color: C.ink }}>
              <option value="consumer">Consumer</option>
              <option value="demo">Demo (test)</option>
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: C.slate, marginBottom: 5 }}>Plan</div>
            <select value={v.plan} onChange={(e) => setV({ ...v, plan: e.target.value })} data-testid="add-sub-plan"
              style={{ width: "100%", padding: "10px 12px", border: "1px solid rgba(26,30,36,0.16)", borderRadius: 8, fontSize: 13, background: "#fff", color: C.ink }}>
              <option value="">— none —</option>
              <option value="single">Single</option>
              <option value="duo">Duo</option>
              <option value="family">Family</option>
            </select>
          </div>
        </div>
        <label style={{ display: "flex", gap: 9, alignItems: "flex-start", background: "#fff", border: `1px solid ${C.navy}22`, borderRadius: 10, padding: 13, marginBottom: 16, cursor: "pointer" }}>
          <input type="checkbox" checked={v.legacy_app_user} onChange={(e) => setV({ ...v, legacy_app_user: e.target.checked })} data-testid="add-sub-legacy" style={{ marginTop: 2 }} />
          <span style={{ fontSize: 12.5, color: C.ink, lineHeight: 1.5 }}>
            <b>Legacy app user — include in the migration wave.</b> Marks them as having activated the old ResQ1 app, which puts them in the migration email audience on the Communications page.
          </span>
        </label>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={onClose} style={{ flex: 1, padding: 12, background: "transparent", border: `1px solid ${C.slate}44`, color: C.slate, borderRadius: 8, fontSize: 13, cursor: "pointer" }}>Cancel</button>
          <button onClick={save} disabled={busy} data-testid="add-sub-save" style={{ flex: 2, padding: 12, background: C.red, color: "#fff", border: 0, borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1 }}>{busy ? "Adding…" : "Add subscriber"}</button>
        </div>
      </div>
    </div>
  );
}

function ActivationChip({ c, i }) {
  const day = (t) => (t ? String(t).slice(0, 10) : null);
  const map = {
    activated: ["✓ Activated", C.green, day(c.password_set_at)],
    opened: ["◑ Opened", C.amber, day(c.invite_opened_at)],
    invited: ["→ Invited", C.navy, day(c.invited_at)],
    none: ["— Not invited", C.slate, null],
  };
  const [label, color, when] = map[c.activation_state] || map.none;
  return (
    <span data-testid={`contact-activation-${i}`} title={when ? `${label} on ${when}` : label}
      style={{ display: "inline-block", padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700, color, background: `${color}14`, border: `1px solid ${color}33`, whiteSpace: "nowrap" }}>
      {label}{when ? <span style={{ fontWeight: 400, opacity: 0.75 }}> · {when.slice(5)}</span> : null}
    </span>
  );
}

function WelcomePackModal({ results, onClose }) {
  const wa = (r) => {
    const first = (r.full_name || "").split(" ")[0];
    const parts = [`Hi ${first}, welcome to ResQ1.`];
    if (r.invite_link && !r.activated) parts.push(`1) Open this on your phone to install the app and set your password: ${r.invite_link}`);
    if (r.do_link) parts.push(`${r.invite_link && !r.activated ? "2)" : "Please"} confirm your debit order here: ${r.do_link}`);
    return `https://wa.me/${String(r.cell || "").replace(/\D/g, "")}?text=${encodeURIComponent(parts.join("\n"))}`;
  };
  const ok = results.filter((r) => r.email_status === "sent" || r.email_status === "simulated").length;
  return (
    <div onClick={onClose} data-testid="welcome-pack-modal" style={{ position: "fixed", inset: 0, background: "rgba(11,30,59,0.45)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 100 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: C.cream, borderRadius: 16, padding: 26, width: 560, maxWidth: "94vw", maxHeight: "88vh", overflowY: "auto" }}>
        <div style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 22, color: C.ink, marginBottom: 4 }}>Welcome pack sent</div>
        <div style={{ fontSize: 12.5, color: C.slate, marginBottom: 14 }} data-testid="welcome-pack-summary">
          {ok} of {results.length} emailed · each pack = app activation link + debit order form in one email. App users also see the debit-order prompt in the app.
        </div>
        {results.map((r) => {
          const sent = r.email_status === "sent" || r.email_status === "simulated";
          return (
            <div key={r.id} data-testid="welcome-pack-row" style={{ background: "#fff", border: `1px solid ${sent ? C.green : C.amber}44`, borderRadius: 10, padding: "12px 14px", marginBottom: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "baseline" }}>
                <div style={{ fontWeight: 700, color: C.ink, fontSize: 13.5 }}>{r.full_name || r.id}</div>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: sent ? C.green : C.amber }}>
                  {r.error ? r.error : sent ? `Emailed ${r.email}` : r.email ? `Email failed (${r.email_status || "no status"})` : "No email on record"}
                </div>
              </div>
              <div style={{ fontSize: 11.5, color: C.slate, marginTop: 3 }}>
                {r.activated ? "App already activated — debit order only" : r.invite_link ? "Activation link included" : "No activation link"}
                {r.do_already_signed ? " · debit order already signed" : r.do_link ? " · debit order form included" : ""}
              </div>
              {r.cell && (r.invite_link || r.do_link) && (
                <a href={wa(r)} target="_blank" rel="noreferrer" data-testid="welcome-pack-whatsapp"
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 8, padding: "7px 12px", background: "#25D366", color: "#fff", borderRadius: 999, fontSize: 12, fontWeight: 700, textDecoration: "none" }}>
                  <PaperPlaneTilt size={12} /> Also send on WhatsApp to {r.cell}</a>
              )}
            </div>
          );
        })}
        <button onClick={onClose} data-testid="welcome-pack-close" style={{ marginTop: 8, width: "100%", padding: 11, background: C.red, color: "#fff", border: 0, borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Done</button>
      </div>
    </div>
  );
}

function InviteModal({ invite, onClose }) {
  const sent = invite.email_status === "sent";
  const copy = () => { navigator.clipboard.writeText(invite.link).then(() => toast.success("Link copied")).catch(() => toast.error("Copy failed — select and copy manually")); };
  const fallbackNote = invite.email_status === "blocked"
    ? `Email to ${invite.email} is held by the safety allowlist until go-live. Share this link manually for now.`
    : invite.email
      ? `We couldn't email ${invite.email}${invite.email_status ? ` (${invite.email_status})` : ""}. Share this link manually.`
      : "No email address on file for this subscriber. Share this link manually.";
  return (
    <div onClick={onClose} data-testid="invite-link-modal" style={{ position: "fixed", inset: 0, background: "rgba(11,30,59,0.45)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 100 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 440, maxWidth: "92%", background: C.body, borderRadius: 14, padding: 24 }}>
        <div style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 22, color: C.ink, marginBottom: 4 }}>Invite {invite.full_name || "subscriber"}</div>
        {sent ? (
          <div style={{ display: "flex", alignItems: "flex-start", gap: 11, background: `${C.green}12`, border: `1px solid ${C.green}44`, borderRadius: 10, padding: 14, margin: "14px 0 4px" }} data-testid="invite-email-sent">
            <CheckCircle size={22} weight="fill" color={C.green} style={{ flexShrink: 0, marginTop: 1 }} />
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>Invite email sent</div>
              <div style={{ fontSize: 12.5, color: C.slate, marginTop: 2, lineHeight: 1.5 }}>
                We emailed {invite.email ? <b style={{ color: C.ink }}>{invite.email}</b> : "the subscriber"} a link to download and set up the ResQ1 app.
              </div>
            </div>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 12.5, color: C.slate, margin: "6px 0 14px", lineHeight: 1.5 }} data-testid="invite-fallback-note">{fallbackNote}</div>
            <div style={{ background: "#fff", border: "1px solid rgba(26,30,36,0.12)", borderRadius: 8, padding: "10px 12px", fontSize: 12, fontFamily: "monospace", color: C.ink, wordBreak: "break-all", marginBottom: 12 }} data-testid="invite-link-text">{invite.link}</div>
            {invite.cell && (
              <a href={`https://wa.me/${String(invite.cell).replace(/\D/g, "")}?text=${encodeURIComponent(`Hi ${(invite.full_name || "").split(" ")[0]}, here's your ResQ1 app link. Open it on your phone to set a password and arm your panic button: ${invite.link}`)}`}
                target="_blank" rel="noreferrer" data-testid="invite-whatsapp-btn"
                style={{ display: "block", width: "100%", padding: 12, marginBottom: 8, background: C.green, color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 700, textAlign: "center", textDecoration: "none" }}>
                Send on WhatsApp to {invite.cell}
              </a>
            )}
            <button onClick={copy} data-testid="invite-copy-btn" style={{ width: "100%", padding: 12, background: C.ink, color: "#fff", border: 0, borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Copy link</button>
          </>
        )}
        <button onClick={onClose} data-testid="invite-close-btn" style={{ marginTop: 10, width: "100%", padding: 11, background: sent ? C.red : "transparent", color: sent ? "#fff" : C.slate, border: sent ? 0 : `1px solid ${C.slate}44`, borderRadius: 8, fontSize: 13, fontWeight: sent ? 700 : 400, cursor: "pointer" }}>{sent ? "Done" : "Close"}</button>
      </div>
    </div>
  );
}

export function HistoryModal({ data, onClose }) {
  const [filter, setFilter] = useState("all");
  const CATS = {
    emergency: { label: "Emergencies", color: C.red },
    invite: { label: "App & invites", color: C.navy },
    dependant: { label: "Dependants", color: C.green },
    email: { label: "Emails", color: "#007AFF" },
    cover: { label: "Cover & billing", color: C.amber },
    record: { label: "Record", color: C.slate },
    legacy: { label: "Legacy help requests", color: C.slate },
  };
  const counts = data.counts || {};
  const chips = [["all", `Everything (${data.count})`]].concat(
    Object.keys(CATS).filter((k) => counts[k]).map((k) => [k, `${CATS[k].label} (${counts[k]})`]));
  const events = (data.events || []).filter((e) => filter === "all" || e.category === filter);
  const when = (v) => {
    const d = new Date(v);
    return isNaN(d) ? String(v).slice(0, 16).replace("T", " ")
      : d.toLocaleString("en-ZA", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };
  return (
    <div onClick={onClose} data-testid="history-modal" style={{ position: "fixed", inset: 0, background: "rgba(11,30,59,0.45)", display: "flex", justifyContent: "flex-end", zIndex: 100 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 540, maxWidth: "100%", background: C.body, height: "100%", overflowY: "auto", padding: 26, boxShadow: "-20px 0 60px rgba(0,0,0,0.2)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: C.slate, fontFamily: "'JetBrains Mono', monospace" }}>Member history</div>
            <div style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 24, color: C.ink, marginTop: 4 }}>{data.contact.full_name}</div>
            <div style={{ fontSize: 12, color: C.slate }} data-testid="history-summary">
              {[data.contact.insurer, data.contact.policy_number, `${data.count} event${data.count === 1 ? "" : "s"}`].filter(Boolean).join(" · ")}
              {data.first_at ? ` · since ${String(data.first_at).slice(0, 10)}` : ""}
            </div>
          </div>
          <button onClick={onClose} data-testid="history-close" style={{ padding: 8, border: "1px solid rgba(26,30,36,0.12)", borderRadius: 8, background: "#fff", cursor: "pointer" }}><X size={16} /></button>
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
          {chips.map(([k, label]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`history-filter-${k}`}
              style={{
                padding: "5px 11px", borderRadius: 999, fontSize: 11.5, fontWeight: 600, cursor: "pointer",
                border: `1px solid ${filter === k ? (k === "all" ? C.navy : CATS[k].color) : "rgba(26,30,36,0.14)"}`,
                background: filter === k ? (k === "all" ? C.navy : CATS[k].color) : "#fff",
                color: filter === k ? "#fff" : C.slate,
              }}>{label}</button>
          ))}
        </div>

        {events.length === 0 && (
          <div style={{ fontSize: 13, color: C.slate, padding: "20px 0" }} data-testid="history-empty">
            Nothing on record for this member yet.
          </div>
        )}
        {events.map((e, i) => {
          const cat = CATS[e.category] || CATS.record;
          return (
            <div key={`${e.at}-${i}`} data-testid={`history-item-${i}`} style={{ background: "#fff", border: "1px solid rgba(26,30,36,0.08)", borderRadius: 10, padding: 14, marginBottom: 10, borderLeft: `3px solid ${cat.color}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 4 }}>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: C.slate }}>{when(e.at)}</span>
                <span style={{ fontSize: 10, fontWeight: 700, color: cat.color, textTransform: "uppercase", letterSpacing: "0.08em", flexShrink: 0 }}>{cat.label}</span>
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: C.ink, minWidth: 0, overflowWrap: "anywhere" }}>{e.title}</div>
              {e.detail && <div style={{ fontSize: 12.5, color: C.slate, marginTop: 3, lineHeight: 1.5, overflowWrap: "anywhere" }}>{e.detail}</div>}
              {(e.ref || e.who) && (
                <div style={{ fontSize: 11, color: C.slate, marginTop: 6, fontFamily: "'JetBrains Mono', monospace", overflowWrap: "anywhere" }}>
                  {[e.ref, e.who].filter(Boolean).join(" · ")}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function UnmatchedPanel({ data, onClose, onView, onPurge }) {
  const [confirm, setConfirm] = useState(false);
  return (
    <div onClick={onClose} data-testid="unmatched-panel" style={{ position: "fixed", inset: 0, background: "rgba(11,30,59,0.45)", display: "flex", justifyContent: "flex-end", zIndex: 110 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 520, maxWidth: "100%", background: C.body, height: "100%", overflowY: "auto", padding: 26, boxShadow: "-20px 0 60px rgba(0,0,0,0.2)", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
          <div>
            <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: C.slate, fontFamily: "'JetBrains Mono', monospace" }}>Orphaned legacy history</div>
            <div style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 24, color: C.ink, marginTop: 4 }}>Unlinked help requests</div>
            <div style={{ fontSize: 12, color: C.slate }}>{data.row_count.toLocaleString()} records · {data.people_count.toLocaleString()} people · no matching subscriber</div>
          </div>
          <button onClick={onClose} data-testid="unmatched-close" style={{ padding: 8, border: "1px solid rgba(26,30,36,0.12)", borderRadius: 8, background: "#fff", cursor: "pointer" }}><X size={16} /></button>
        </div>

        <div style={{ flex: 1, marginTop: 14 }}>
          {data.people.map((p, i) => (
            <div key={p.key} data-testid={`unmatched-person-${i}`} style={{ background: "#fff", border: "1px solid rgba(26,30,36,0.08)", borderRadius: 10, padding: "12px 14px", marginBottom: 8, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.ink }}>{p.full_name}</div>
                <div style={{ fontSize: 12, color: C.slate, fontFamily: "'JetBrains Mono', monospace" }}>{[p.cell, p.id_number, p.insurer].filter(Boolean).join(" · ")}</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: C.slate, fontVariantNumeric: "tabular-nums" }}>{p.count}</span>
                <button onClick={() => onView(p)} data-testid={`unmatched-view-${i}`} style={{ padding: "6px 12px", background: `${C.navy}0a`, border: `1px solid ${C.navy}22`, color: C.navy, borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>View</button>
              </div>
            </div>
          ))}
        </div>

        <div style={{ borderTop: `1px solid ${C.ink}12`, paddingTop: 16, marginTop: 8 }}>
          {!confirm ? (
            <button onClick={() => setConfirm(true)} data-testid="unmatched-dispose-btn"
              style={{ width: "100%", padding: "12px 16px", background: C.red, color: "#fff", border: 0, borderRadius: 999, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Dispose all orphaned records</button>
          ) : (
            <div data-testid="unmatched-confirm">
              <div style={{ fontSize: 13, color: C.ink, marginBottom: 10 }}>Permanently delete <b>{data.row_count.toLocaleString()}</b> records? They will be archived first (reversible).</div>
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={onPurge} data-testid="unmatched-confirm-yes" style={{ flex: 1, padding: "11px 16px", background: C.red, color: "#fff", border: 0, borderRadius: 999, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Yes, dispose</button>
                <button onClick={() => setConfirm(false)} data-testid="unmatched-confirm-no" style={{ flex: 1, padding: "11px 16px", background: "transparent", color: C.slate, border: `1px solid ${C.slate}44`, borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MemberField({ label, k, opts, f, set }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, color: C.slate, marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.08em" }}>{label}</div>
      {opts ? (
        <select value={f[k]} onChange={set(k)} data-testid={`member-${k}`} style={{ width: "100%", padding: "10px 12px", border: "1px solid rgba(26,30,36,0.16)", borderRadius: 8, fontSize: 13, background: "#fff", color: C.ink }}>{opts.map((o) => <option key={o} value={o}>{o}</option>)}</select>
      ) : (
        <input value={f[k]} onChange={set(k)} data-testid={`member-${k}`} style={{ width: "100%", padding: "10px 12px", border: "1px solid rgba(26,30,36,0.16)", borderRadius: 8, fontSize: 13, outline: "none", color: C.ink }} />
      )}
    </div>
  );
}

function MemberDrawer({ contact, onClose, onSaved, onHistory, onInvite, onPack }) {
  const [packing, setPacking] = useState(false);
  const [dl, setDl] = useState(false);
  const downloadProfile = async () => {
    setDl(true);
    try {
      const r = await api.get(`/contacts/${contact.id}/profile.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `profile-${(contact.full_name || "subscriber").replace(/[^A-Za-z0-9]+/g, "-").toLowerCase()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Couldn't download the profile"); }
    finally { setDl(false); }
  };
  const sendPackFromDrawer = async () => {
    setPacking(true);
    try {
      // Uses the email in the field (saved first) — the fix for "member changed their email address".
      const { data } = await api.post(`/contacts/${contact.id}/welcome-pack`, { email: f.email || null });
      onPack && onPack(data);
    } catch (e) { toast.error("Couldn't send welcome pack", { description: e.response?.data?.detail || e.message }); }
    finally { setPacking(false); }
  };
  const [f, setF] = useState({
    full_name: contact.full_name || "", cell: contact.cell || "", email: contact.email || "",
    insurer: contact.insurer || "", product: contact.product || "consumer",
    policy_number: contact.policy_number || "", member_type: contact.member_type || "main",
    relationship: contact.relationship || "",
  });
  const [fam, setFam] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [showDep, setShowDep] = useState(false);
  const [dep, setDep] = useState({ full_name: "", cell: "", id_number: "", email: "", relationship: "Spouse" });
  const reloadFam = () => api.get(`/contacts/${contact.id}/family`).then((r) => setFam(r.data)).catch(() => {});
  const addDep = async () => {
    if (!dep.full_name.trim()) return toast.error("Dependant name is required");
    setBusy(true);
    try {
      const { data } = await api.post(`/contacts/${contact.id}/dependants`, dep);
      toast.success(`${data.full_name} added to policy ${data.policy_number}`);
      setDep({ full_name: "", cell: "", id_number: "", email: "", relationship: "Spouse" });
      setShowDep(false);
      await reloadFam();
      onSaved && onSaved();
    } catch (e) { toast.error("Couldn't add dependant", { description: e.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };
  const removeDep = async (d) => {
    if (!window.confirm(`Remove ${d.full_name} from this policy?`)) return;
    try {
      await api.delete(`/contacts/${contact.id}/dependants/${d.id}`);
      toast.success("Dependant removed");
      await reloadFam();
      onSaved && onSaved();
    } catch (e) { toast.error("Couldn't remove", { description: e.response?.data?.detail || e.message }); }
  };
  useEffect(() => { api.get(`/contacts/${contact.id}/family`).then((r) => setFam(r.data)).catch(() => setFam({ members: [] })); }, [contact.id]);
  const save = async () => {
    setSaving(true);
    try { await api.put(`/contacts/${contact.id}`, f); toast.success("Member updated"); onSaved && onSaved(); onClose(); }
    catch (e) { toast.error("Update failed", { description: e.response?.data?.detail || e.message }); }
    finally { setSaving(false); }
  };
  const doCancel = async () => {
    const r = reason.trim();
    if (!r) return toast.error("Please enter a cancellation reason");
    setBusy(true);
    try {
      const res = await api.post(`/contacts/${contact.id}/cancel`, { reason: r });
      toast.success(`Subscriber cancelled${res.data.cancelled > 1 ? ` · whole household (${res.data.cancelled})` : ""}`);
      onSaved && onSaved(); onClose();
    } catch (e) { toast.error("Couldn't cancel", { description: e.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };
  const doReactivate = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/contacts/${contact.id}/reactivate`);
      toast.success(`Subscriber reactivated${res.data.reactivated > 1 ? ` · whole household (${res.data.reactivated})` : ""}`);
      onSaved && onSaved(); onClose();
    } catch (e) { toast.error("Couldn't reactivate", { description: e.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };
  const deps = (fam?.members || []).filter((m) => m.id !== contact.id);
  const set = (k) => (e) => setF((prev) => ({ ...prev, [k]: e.target.value }));
  return (
    <div onClick={onClose} data-testid="member-drawer" style={{ position: "fixed", inset: 0, background: "rgba(11,30,59,0.45)", display: "flex", justifyContent: "flex-end", zIndex: 100 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 480, maxWidth: "100%", background: C.body, height: "100%", overflowY: "auto", padding: 26, boxShadow: "-20px 0 60px rgba(0,0,0,0.2)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18 }}>
          <div>
            <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: C.slate, fontFamily: "'JetBrains Mono', monospace" }}>Member details</div>
            <div style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 24, color: C.ink, marginTop: 4 }}>{contact.full_name}</div>
          </div>
          <button onClick={onClose} data-testid="member-close" style={{ padding: 8, border: "1px solid rgba(26,30,36,0.12)", borderRadius: 8, background: "#fff", cursor: "pointer" }}><X size={16} /></button>
        </div>
        <MemberField label="Full name" k="full_name" f={f} set={set} />
        <MemberField label="Cell" k="cell" f={f} set={set} />
        <MemberField label="Email" k="email" f={f} set={set} />
        <MemberField label="Insurer" k="insurer" f={f} set={set} />
        <MemberField label="Policy number" k="policy_number" f={f} set={set} />
        <div style={{ display: "flex", gap: 10 }}>
          <div style={{ flex: 1 }}><MemberField label="Product" k="product" opts={["consumer", "commercial", "demo"]} f={f} set={set} /></div>
          <div style={{ flex: 1 }}><MemberField label="Member type" k="member_type" opts={["main", "dependant"]} f={f} set={set} /></div>
        </div>
        <MemberField label="Relationship" k="relationship" f={f} set={set} />
        <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
          <button onClick={save} disabled={saving} data-testid="member-save" style={{ flex: 1, padding: 12, background: C.red, color: "#fff", border: 0, borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: saving ? "default" : "pointer", opacity: saving ? 0.6 : 1 }}>{saving ? "Saving…" : "Save changes"}</button>
          <button onClick={() => onHistory(contact)} data-testid="member-history" style={{ padding: "12px 14px", background: `${C.navy}0a`, border: `1px solid ${C.navy}22`, color: C.navy, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>History</button>
        </div>
        <button onClick={sendPackFromDrawer} disabled={packing} data-testid="member-send-pack"
          style={{ marginTop: 8, width: "100%", padding: 11, display: "flex", alignItems: "center", justifyContent: "center", gap: 7, background: `${C.green}12`, color: C.green, border: `1px solid ${C.green}55`, borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: packing ? "default" : "pointer", opacity: packing ? 0.6 : 1 }}>
          <Package size={15} /> {packing ? "Sending…" : "Send welcome pack (invite + debit order)"}
        </button>
        <div style={{ fontSize: 11, color: C.slate, marginTop: 5, lineHeight: 1.4 }}>Sends to the email in the field above and saves it if it changed. Already-activated members get the debit order form only.</div>
        <button onClick={downloadProfile} disabled={dl} data-testid="member-download-profile"
          style={{ marginTop: 8, width: "100%", padding: 11, display: "flex", alignItems: "center", justifyContent: "center", gap: 7, background: `${C.navy}0a`, color: C.navy, border: `1px solid ${C.navy}33`, borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: dl ? "default" : "pointer", opacity: dl ? 0.6 : 1 }}>
          <DownloadSimple size={15} /> {dl ? "Preparing PDF…" : "Download profile (PDF)"}
        </button>
        <div style={{ fontSize: 11, color: C.slate, marginTop: 5, lineHeight: 1.4 }}>For the debit order provider: member, plan, household and bank details, with the signed debit order authority attached when it's on the system.</div>

        <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.slate}22` }}>
          {contact.cancelled ? (
            <div data-testid="member-cancelled-banner" style={{ background: `${C.amber}14`, border: `1px solid ${C.amber}55`, borderRadius: 10, padding: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.ink, marginBottom: 4 }}>Subscription cancelled</div>
              <div style={{ fontSize: 12, color: C.slate, lineHeight: 1.5 }}>
                Emergency response is OFF for this {contact.policy_number ? "policy household" : "member"}.
                {contact.cancel_reason ? <> Reason: <b style={{ color: C.ink }}>{contact.cancel_reason}</b>.</> : null}
              </div>
              <button onClick={doReactivate} disabled={busy} data-testid="member-reactivate"
                style={{ marginTop: 10, width: "100%", padding: 11, background: C.green, color: "#fff", border: 0, borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1 }}>
                {busy ? "Please wait…" : "Reactivate subscriber"}
              </button>
            </div>
          ) : !showCancel ? (
            <button onClick={() => setShowCancel(true)} data-testid="member-cancel-open"
              style={{ width: "100%", padding: 11, background: "transparent", color: C.red, border: `1px solid ${C.red}55`, borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
              Cancel subscriber
            </button>
          ) : (
            <div data-testid="member-cancel-form" style={{ background: `${C.red}0a`, border: `1px solid ${C.red}33`, borderRadius: 10, padding: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.ink, marginBottom: 4 }}>Cancel this subscriber?</div>
              <div style={{ fontSize: 12, color: C.slate, marginBottom: 10, lineHeight: 1.5 }}>
                Turns OFF emergency response{contact.policy_number ? " for the whole policy household" : ""}. The account stays usable and you can reactivate anytime.
              </div>
              <div style={{ fontSize: 11, color: C.slate, marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.08em" }}>Reason (required)</div>
              <select value={CANCEL_REASONS.includes(reason) ? reason : (reason ? "Other" : "")}
                onChange={(e) => setReason(e.target.value === "Other" ? "" : e.target.value)} data-testid="member-cancel-reason-select"
                style={{ width: "100%", padding: "10px 12px", border: "1px solid rgba(26,30,36,0.16)", borderRadius: 8, fontSize: 13, background: "#fff", color: C.ink, marginBottom: 8 }}>
                <option value="">Select a reason…</option>
                {CANCEL_REASONS.map((p) => <option key={p} value={p}>{p}</option>)}
                <option value="Other">Other (type below)</option>
              </select>
              <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Cancellation reason" data-testid="member-cancel-reason"
                style={{ width: "100%", padding: "10px 12px", border: "1px solid rgba(26,30,36,0.16)", borderRadius: 8, fontSize: 13, outline: "none", color: C.ink, marginBottom: 10 }} />
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => { setShowCancel(false); setReason(""); }} data-testid="member-cancel-abort"
                  style={{ flex: 1, padding: 11, background: "transparent", border: `1px solid ${C.slate}44`, color: C.slate, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Keep active</button>
                <button onClick={doCancel} disabled={busy || !reason.trim()} data-testid="member-cancel-confirm"
                  style={{ flex: 1, padding: 11, background: C.red, color: "#fff", border: 0, borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: (busy || !reason.trim()) ? "default" : "pointer", opacity: (busy || !reason.trim()) ? 0.6 : 1 }}>
                  {busy ? "Cancelling…" : "Confirm cancel"}
                </button>
              </div>
            </div>
          )}
        </div>

        {(contact.cancellation_log || []).length > 0 && (
          <div style={{ marginTop: 22 }} data-testid="member-cancel-log">
            <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: C.slate, fontFamily: "'JetBrains Mono', monospace", marginBottom: 10 }}>Cancellation history</div>
            {[...contact.cancellation_log].reverse().map((e, i) => (
              <div key={i} data-testid={`member-cancel-log-${i}`} style={{ display: "flex", gap: 10, background: "#fff", border: "1px solid rgba(26,30,36,0.08)", borderRadius: 10, padding: "10px 12px", marginBottom: 8 }}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", marginTop: 5, flexShrink: 0, background: e.action === "cancelled" ? C.red : C.green }} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.ink, textTransform: "capitalize" }}>
                    {e.action}{e.reason ? <span style={{ fontWeight: 400, color: C.slate }}> · {e.reason}</span> : null}
                  </div>
                  <div style={{ fontSize: 11, color: C.slate, marginTop: 2 }}>
                    {e.at ? new Date(e.at).toLocaleString("en-ZA", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}{e.by ? ` · ${e.by}` : ""}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={{ marginTop: 22 }}>
          <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: C.slate, fontFamily: "'JetBrains Mono', monospace", marginBottom: 10 }}>Dependents{fam?.policy_number ? ` · policy ${fam.policy_number}` : ""}</div>
          {!fam && <div style={{ fontSize: 13, color: C.slate }}>Loading…</div>}
          {fam && deps.length === 0 && <div style={{ fontSize: 13, color: C.slate }} data-testid="member-no-deps">No dependents on this policy.</div>}
          {deps.map((d, i) => {
            const state = d.password_set_at || d.app_user_id ? ["✓ Activated", C.green]
              : d.invite_opened_at ? ["◑ Opened", C.amber]
              : d.invited_at ? ["→ Invited", C.navy] : ["— Not invited", C.slate];
            return (
            <div key={d.id} data-testid={`member-dep-${i}`} style={{ background: "#fff", border: "1px solid rgba(26,30,36,0.08)", borderRadius: 10, padding: "11px 13px", marginBottom: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: C.ink }}>{d.full_name}</div>
                  <div style={{ fontSize: 11, color: C.slate }}>{[d.relationship || d.member_type, d.cell, d.email].filter(Boolean).join(" · ")}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                  <span data-testid={`member-dep-state-${i}`} style={{ fontSize: 11, fontWeight: 700, color: state[1] }}>{state[0]}</span>
                  {d.member_type === "dependant" && (
                    <button onClick={() => removeDep(d)} data-testid={`member-dep-remove-${i}`} title="Remove dependant"
                      style={{ background: "none", border: 0, color: C.slate, cursor: "pointer", padding: 2, lineHeight: 1 }}><X size={14} /></button>
                  )}
                </div>
              </div>
              <button onClick={() => onInvite && onInvite(d)} data-testid={`member-dep-invite-${i}`}
                style={{ marginTop: 9, width: "100%", padding: 9, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, background: `${C.red}0f`, border: `1px solid ${C.red}44`, color: C.red, borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                <PaperPlaneTilt size={13} /> {d.invited_at ? "Re-send their app invite" : "Invite them to the app"}
              </button>
            </div>
          ); })}
          {!showDep ? (
            <button onClick={() => setShowDep(true)} data-testid="member-add-dep-open"
              style={{ marginTop: 4, width: "100%", padding: 11, background: "transparent", color: C.navy, border: `1px dashed ${C.navy}44`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              + Add a dependant to this policy
            </button>
          ) : (
            <div data-testid="member-add-dep-form" style={{ background: "#fff", border: `1px solid ${C.navy}22`, borderRadius: 10, padding: 14, marginTop: 4 }}>
              <div style={{ fontSize: 12, color: C.slate, marginBottom: 10, lineHeight: 1.5 }}>
                They&apos;re added under {fam?.policy_number ? <>policy <b style={{ color: C.ink }}>{fam.policy_number}</b></> : "a new policy number"} and will be named in the migration email as covered.
              </div>
              {[["full_name", "Full name"], ["cell", "Cell (optional)"], ["email", "Email (optional — needed to email their invite)"], ["id_number", "ID number (optional)"]].map(([k, label]) => (
                <input key={k} value={dep[k]} onChange={(e) => setDep({ ...dep, [k]: e.target.value })} placeholder={label} data-testid={`dep-${k}`}
                  style={{ width: "100%", padding: "10px 12px", border: "1px solid rgba(26,30,36,0.16)", borderRadius: 8, fontSize: 13, outline: "none", color: C.ink, marginBottom: 8 }} />
              ))}
              <select value={dep.relationship} onChange={(e) => setDep({ ...dep, relationship: e.target.value })} data-testid="dep-relationship"
                style={{ width: "100%", padding: "10px 12px", border: "1px solid rgba(26,30,36,0.16)", borderRadius: 8, fontSize: 13, background: "#fff", color: C.ink, marginBottom: 10 }}>
                {["Spouse", "Child", "Parent", "Dependant", "Other"].map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => setShowDep(false)} style={{ flex: 1, padding: 11, background: "transparent", border: `1px solid ${C.slate}44`, color: C.slate, borderRadius: 8, fontSize: 13, cursor: "pointer" }}>Cancel</button>
                <button onClick={addDep} disabled={busy} data-testid="dep-save"
                  style={{ flex: 1.4, padding: 11, background: C.navy, color: "#fff", border: 0, borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1 }}>{busy ? "Adding…" : "Add dependant"}</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Td = ({ children, mono, style }) => (
  <td style={{ padding: "12px 16px", borderTop: "1px solid rgba(26,30,36,0.06)", color: "#1A1E24", fontFamily: mono ? "'JetBrains Mono', monospace" : undefined, fontSize: mono ? 12 : 13, ...style }}>{children}</td>
);
const Select = ({ value, onChange, opts, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
    style={{ padding: "9px 12px", border: "1px solid rgba(26,30,36,0.14)", borderRadius: 8, fontSize: 13, background: "#fff", color: "#1A1E24", cursor: "pointer" }}>
    {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);
const PageBtn = ({ children, disabled, onClick, testid }) => (
  <button onClick={onClick} disabled={disabled} data-testid={testid}
    style={{ padding: "7px 14px", border: "1px solid rgba(26,30,36,0.14)", borderRadius: 8, background: "#fff", fontSize: 13, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.4 : 1, color: "#1A1E24" }}>{children}</button>
);
